export default {
  "admin": {
    "discounts": {
      "calc": {
        "byPercent": "By percentage",
        "discountedSeats": "Discounted seats (of {{total}})",
        "equivalent": "Equals",
        "fromSeats": "From seat math",
        "offPercent": "Off-percentage on those seats (%)",
      },
      "compact": {
        "activeBadge": "{{percent}}% active",
        "duration_one": "{{count}}-month duration",
        "duration_other": "{{count}}-month duration",
        "remove": "Remove",
      },
      "duration": {
        "forever": "Forever",
        "months_one": "{{count}} month",
        "months_other": "{{count}} months",
      },
      "form": {
        "duration": {
          "description": "After this window, sales re-evaluates the discount.",
          "label": "Duration",
        },
        "percent": {
          "description": "Sales enters a value between 1 and 100.",
          "label": "Discount percentage",
        },
        "reason": {
          "description": "Stored in the coupon metadata for audit.",
          "label": "Reason",
          "placeholder": 'e.g. "Chair used as a room, 50% off one seat"',
        },
        "submit": "Apply discount",
        "title": "Apply new discount",
      },
      "preview": {
        "discount": "Discount ({{percent}}%)",
        "newTotal": "New invoice total",
        "subtotal": "Subtotal",
        "title": "Preview for the invoice on {{date}}",
      },
      "sectionTitle": "Manage discount",
      "subheading":
        "The discount is applied directly to the live subscription. The next invoice is automatically reduced.",
      "trialingBanner": {
        "body":
          "The subscription is still in its trial. Any discount applied here activates on the first invoice on {{date}}.",
        "title": "Discount will activate after the trial",
      },
      "warnings": {
        "fetchFailed": {
          "body":
            "Dentero could not confirm whether Stripe already has a discount. Do not apply or remove discounts until this loads successfully.",
          "title": "Could not verify Stripe discounts",
        },
        "sharedSubscription": {
          "body":
            "This subscription is attached to multiple practices. Any discount affects all of them, so coordinate with finance before changing it.",
          "title": "Shared subscription",
        },
      },
    },
    "onboardingPackage": {
      "amount": "Amount",
      "bookedAt": "Booked on",
      "invoice": "Invoice",
      "noInvoice": "No invoice",
      "note": {
        "label": "Note",
        "placeholder": "Internal note, for example what is still missing",
      },
      "openInvoice": "Open in Stripe",
      "plusVat": "plus VAT",
      "save": "Save",
      "status": "Status",
      "statuses": {
        "accepted": "Accepted",
        "documents_received": "Documents received",
        "first_call_done": "First call done",
        "refunded": "Refunded",
        "requested_documents": "Documents requested",
        "setup_in_progress": "Setup in progress",
      },
      "subheadingCheckout":
        "Booked at checkout. Our team sets the status by hand.",
      "subheadingInvoice":
        "Booked by invoice. Our team sets the status by hand.",
      "title": "Onboarding package",
    },
    "settings": {
      "androidAppVersion": {
        "description": "Latest released version of the Android app.",
        "label": "Android app version",
        "placeholder": "e.g. 1.2.3",
      },
      "androidStoreUrl": {
        "description": "Link to the app's Play Store listing.",
        "label": "Android store link",
        "placeholder": "https://play.google.com/store/apps/details?id=...",
      },
      "complexAutoApplyThreshold": {
        "description":
          "At or above this confidence (0 to 1), a complex with automatic application enabled is merged in. Below it, the complex stays a suggestion.",
        "label": "Complex threshold",
      },
      "documentationModel": {
        "description":
          "Used for improved and structured treatment documentation.",
        "label": "Documentation",
        "placeholder": "Select model",
      },
      "featureSpotlights": {
        "description":
          "Shows users a card about a feature in their plan they have never used. After you turn it off, cards disappear once the page is reloaded. Admins never get a card themselves. To preview one, add ?spotlight_preview= and the key realtime_transcription, complexes_setup, complexes_use, befund_chart_live, befund_chart_review or befund_chart_history to any page, for example /recordings?spotlight_preview=complexes_use. The preview shows the card even while spotlights are off, and records no choice and no statistics.",
        "label": "Feature spotlights",
      },
      "geminiPriorityTier": {
        "description":
          "Requests Priority PayGo for every Gemini call on Vertex (EU). It costs 1.8x the standard rate. When Vertex has no spare capacity it serves the request at the standard rate. Turn it off to save cost.",
        "label": "Gemini Priority",
      },
      "iosAppVersion": {
        "description": "Latest released version of the iOS app.",
        "label": "iOS app version",
        "placeholder": "e.g. 1.2.3",
      },
      "iosStoreUrl": {
        "description": "Link to the app's App Store listing.",
        "label": "iOS store link",
        "placeholder": "https://apps.apple.com/app/...",
      },
      "plausibilityCheckModel": {
        "description":
          "Used for the documentation plausibility check. luna is much cheaper than terra, sol is ~5x more expensive.",
        "label": "Plausibility check",
        "placeholder": "Select model",
      },
      "pseudonymization": {
        "description":
          "Replaces personal data (names, dates of birth, addresses, phone numbers, email addresses, insurance numbers) in the transcript with placeholders. Runs as its own step on Gemini Flash (EU). Applies only to ASR providers (Soniox, Mistral, AssemblyAI); Gemini transcription already pseudonymizes in-prompt. Turning it off stores transcripts un-redacted. Use it only when the provider is blocking transcript writes.",
        "label": "Pseudonymization",
      },
      "sections": {
        "aiModels": {
          "description":
            "Providers and models used for transcription, documentation and billing suggestions.",
          "title": "AI models",
        },
        "appVersions": {
          "description":
            "Latest released app versions and store links shown in the update toast.",
          "title": "App versions",
        },
        "inAppNotices": {
          "description": "Notices users see inside the app.",
          "title": "In-app notices",
        },
      },
      "suggestionModel": {
        "description": "Used for PKV and GKV billing suggestions.",
        "label": "Billing suggestions",
        "placeholder": "Select model",
      },
      "transcriptCorrection": {
        "description":
          "Has a second model check the transcript against the audio for clinical terminology. Applies only to ASR providers (Soniox, Mistral, AssemblyAI); has no effect on Gemini transcription. Turning it off lowers cost and keeps plain ASR transcription.",
        "label": "Transcript correction",
      },
      "transcriptionProvider": {
        "description": "Used for transcribing new recordings.",
        "label": "Transcription",
        "placeholder": "Select provider",
      },
      "transcriptionThinkingLevel": {
        "description":
          'Gemini reasoning effort for transcribing new recordings. "Model default" sends no thinking configuration and lets the model decide.',
        "label": "Transcription thinking level",
        "levels": {
          "default": "Model default",
          "HIGH": "High",
          "LOW": "Low",
          "MEDIUM": "Medium",
          "MINIMAL": "Minimal",
        },
        "placeholder": "Select thinking level",
      },
    },
    "subscriptionAdmin": {
      "disabled": {
        "discount": {
          "invalidStatus":
            "Discounts can only be changed for active subscriptions or during the regular Stripe trial.",
          "legacy":
            "Migrate this legacy subscription to tier pricing before applying Dentero-managed discounts.",
          "noSubscription":
            "Create a Stripe subscription before applying discounts.",
        },
        "provision": {
          "hasSubscription": "This practice already has a Stripe subscription.",
          "noPermission":
            "You do not have permission to create a Stripe checkout link.",
        },
        "trial": {
          "legacy":
            "Migrate this legacy subscription to tier pricing before granting a tier trial.",
          "noSubscription":
            "Create a Stripe subscription before granting tier trials.",
          "notActive":
            "Tier trials can only be granted for active paid subscriptions.",
          "trialing":
            "The regular Stripe trial already grants full commercial access.",
        },
      },
      "tabs": {
        "discount": "Discount",
        "overview": "Overview",
        "provision": "Provision",
        "trial": "Tier trial",
      },
    },
    "trials": {
      "active": {
        "endEarly": "End now",
        "expiresAt": "Expires on",
        "expiresInDays_one": "Expires in {{count}} day",
        "expiresInDays_other": "Expires in {{count}} days",
        "grantedBy": "Granted by",
        "reason": "Reason",
        "tier": "Trialed tier",
        "title": "Active trial",
      },
      "form": {
        "duration": {
          "description": "Auto-reverts on {{expires}}.",
          "label": "Duration",
          "preset_one": "{{count}} day",
          "preset_other": "{{count}} days",
        },
        "reason": {
          "description":
            "Stored on the practice for audit. Do not include patient data.",
          "label": "Reason",
          "placeholder":
            'e.g. "Customer wants to evaluate Pro before committing"',
        },
        "submit": "Grant trial",
        "tier": {
          "label": "Which tier should be trialed?",
          "perMonth": "month",
        },
        "title": "Grant new tier trial",
      },
      "preview": {
        "billingUnchanged": "billing stays on the current paid tier",
        "current": "Currently:",
        "duringTrial": "During the trial:",
        "noTierSelected": "No tier selected yet.",
        "revertsOn": "Reverts on",
        "title": "Preview",
      },
      "sectionTitle": "Tier trials",
      "subheading":
        "Temporary Dentero feature access that auto-reverts. Stripe billing stays unchanged.",
    },
  },
  "aiRequests": {
    "attemptsCount_one": "{{count}} attempt",
    "attemptsCount_other": "{{count}} attempts",
    "columns": {
      "actions": "Actions",
      "cost": "Cost",
      "createdAt": "Created at",
      "duration": "Duration",
      "status": "Status",
      "type": "Type",
    },
    "costUnknown": "Unknown",
    "dialog": {
      "compactedNotice":
        "Debug payloads and unused response fields were removed after 7 days. Cost metadata is kept.",
      "noRawResponse": "No raw response available.",
      "noTextData": "No text data available.",
      "noTextProperties": "No text properties found.",
      "noTraceData": "No trace data available.",
      "triggerLabel": "AI",
    },
    "durationBreakdown": {
      "processing": "File processing: {{seconds}}s",
      "transcribe": "Transcription call: {{seconds}}s",
    },
    "openInLangfuse": "Open trace in Langfuse",
    "openSessionInLangfuse": "Open session in Langfuse",
    "openSessionInLangfuseLegacy":
      "Open session without a run number in Langfuse",
    "openSessionInLangfuseRun": "Open session {{run}} in Langfuse",
    "statusLabels": {
      "completed": "Completed",
      "failed": "Failed",
      "pending": "Pending",
      "unknown": "Unknown",
    },
    "title": "AI requests",
    "toggleHistory": "Toggle history",
    "totalCost": "{{cost}}",
    "totalDuration": "{{seconds}} seconds",
    "typeLabels": {
      "ComplexTemplateRendering": "Complex templates",
      "GkvAnalogSuggestion": "GKV analog suggestion",
      "GkvAnnotation": "GKV annotation",
      "GkvLaboratoryServiceSuggestion": "GKV laboratory service suggestion",
      "GkvSuggestion": "GKV suggestion",
      "ImprovedDocumentation": "Improved documentation",
      "MaterialDetection": "Material detection",
      "PatientBriefing": "Patient briefing",
      "PkvAnalogSuggestion": "PKV analog suggestion",
      "PkvAnnotation": "PKV annotation",
      "PkvLaboratoryServiceSuggestion": "PKV laboratory service suggestion",
      "PkvSuggestion": "PKV suggestion",
      "PlausibilityCheck": "Plausibility check",
      "Pseudonymization": "Pseudonymization",
      "StructuredDocumentation": "Structured documentation",
      "TranscriptCorrection": "Transcript correction",
      "Transcription": "Transcription",
    },
    "unpricedRequestsNotice_one": "({{count}} request could not be priced)",
    "unpricedRequestsNotice_other": "({{count}} requests could not be priced)",
    "words": {
      "count": "Words:",
      "duration": "Duration:",
      "hint": "Hover a word for confidence + timestamp.",
      "legendHigh": "high (≥ {{ceiling}})",
      "legendLow": "low confidence",
      "legendMedium": "medium",
      "noData": "No word-level timing data on this request.",
      "overallConfidence": "Overall confidence:",
      "tooltip": "confidence {{confidence}}% · {{start}}–{{end}}",
      "tooltipPlayable":
        "confidence {{confidence}}% · {{start}}–{{end}} · click to play",
      "tooltipWithSpeaker":
        "confidence {{confidence}}% · {{start}}–{{end}} · speaker {{speaker}}",
      "tooltipWithSpeakerPlayable":
        "confidence {{confidence}}% · {{start}}–{{end}} · speaker {{speaker}} · click to play",
    },
  },
  "analogPositionImports": {
    "new": {
      "description":
        "Loads analog positions from a CSV file into this practice. Every row is shown for review before anything is imported.",
      "downloadTemplate": "Download template",
      "fileLabel": "CSV file",
      "formatHint":
        "One row per analog position. Each description must be unique in the file. Regelwerk is GOZ or GOÄ, and the factor is between 1.0 and 3.5. Separator is semicolon or comma, encoding UTF-8.",
      "submit": "Check file",
    },
    "preview": {
      "columns": {
        "amount": "Amount",
        "description": "Description",
        "factor": "Factor",
        "reference": "Reference position",
        "status": "Status",
      },
      "description":
        "Review the rows and adjust the factor if needed. Only selected rows are imported.",
      "selectAll": "Select all",
      "selectRow": "Select row",
      "submit_one": "Import {{count}} position",
      "submit_other": "Import {{count}} positions",
      "title": "Review import",
    },
    "status": {
      "error": "Error",
      "new": "New",
      "unchanged": "Unchanged",
      "update": "Change",
    },
    "title": "Import analog positions",
  },
  "analogPositions": {
    "details": {
      "deleteDescription":
        "The analog position will be removed from the catalog. Associated practice setups will be deleted. Billing positions already recorded will be retained but will no longer be linked to this setup.",
      "deleteTitle": "Delete analog position?",
      "descriptionLabel": "Service description",
      "usageDescription_one":
        "This analog position is configured in {{count}} practice setup.",
      "usageDescription_other":
        "This analog position is configured in {{count}} practice setups.",
      "usageTitle": "Usage",
    },
    "form": {
      "description":
        "The global catalog contains service descriptions only. Costs are configured in practice settings.",
      "descriptionLabel": "Service description",
      "editTitle": "Edit analog position",
      "newTitle": "Create new analog position",
    },
    "index": {
      "add": "Add analog position",
      "description": "Service description",
      "usage": "Practice setups",
    },
  },
  "assistants": {
    "edit": {
      "contentLabel": "Content",
      "contentPlaceholder": "Content for this assistant...",
    },
    "form": {
      "classificationPlaceholder": "Select classification...",
      "description":
        "Select the treatment classification for which you want to create an assistant.",
      "newTitle": "New assistant",
    },
    "index": {
      "add": "Add",
      "deleteDescription":
        "Are you sure you want to delete this assistant? This action cannot be undone.",
      "deleteTitle": "Delete assistant",
      "empty": "No assistants yet.",
      "updatedAt": "Updated:",
    },
    "selector": {
      "sections_one": "{{count}} section",
      "sections_other": "{{count}} sections",
      "title": "Select documentation assistant",
    },
    "teleprompter": {
      "empty": "No content available.",
      "fontSize": {
        "decrease": "Decrease text size",
        "increase": "Increase text size",
      },
      "mode": {
        "read": "Read mode",
        "teleprompter": "Auto-scroll",
      },
      "pause": "Pause",
      "resume": "Resume scrolling",
    },
  },
  "audioRecorder": {
    "chunkPersistenceError": "The recording could not be cached locally.",
    "micFallback":
      "Selected microphone unavailable. Recording with the default microphone instead.",
    "microphoneAccessDenied": "Microphone access not allowed.",
    "microphoneInUse":
      "The microphone is being used by another application. Please close it and try again.",
    "microphoneNoAudio":
      "The microphone is not delivering any audio, so the recording was not started. Close other programs that use the microphone, or select a different microphone.",
    "noMicrophoneFound": "No microphone found.",
    "startFailed": "Could not start recording.",
    "storageQuotaExceeded":
      "Local storage is almost full. Upload or remove saved recordings to free up space.",
    "unsupported": "Audio recording is not available in this browser.",
  },
  "auth": {
    "forgotPassword": {
      "backToLogin": "Back to sign in",
      "submit": "Reset password now",
      "title": "Forgot your password?",
    },
    "resetPassword": {
      "newPassword": "New password",
      "passwordMismatch": "Passwords must match",
      "title": "Change password",
    },
    "signIn": {
      "bookDemo": "Book a demo",
      "forgotPassword": "Forgot password?",
      "noAccessYet": "Don't have access yet?",
      "rememberMe": "Keep me signed in",
      "title": "Sign in to {{brand}}",
    },
  },
  "benchmarks": {
    "artifacts": {
      "noDebug": "No debug artifacts available for this selection yet.",
    },
    "cases": {
      "editableModelInput": "Editable model input",
      "expectedLabels": "Expected labels",
      "openFullInput": "Open full input",
      "readOnlyPreview":
        "Read-only preview of the user message that will be sent to the model.",
      "review": "Review",
      "treatment": "Treatment",
    },
    "edit": {
      "editFullSystemMessage": "Edit full system message",
      "meta":
        "{{insurance}} · {{model}} · {{sampleCount}} cases · Created by {{creator}} · {{createdAt}}",
      "run": "Run benchmark",
    },
    "new": {
      "caseCount": "Case count",
      "lockedSetup":
        "The benchmark setup is locked after preparation. If you want a different {{insurance}} run later, prepare a new benchmark.",
      "model": "Model",
      "prepare": "Prepare benchmark",
    },
    "runSummary": {
      "cases": "Cases",
      "created": "Created",
      "creator": "Creator",
      "failureMessage": "Failure message",
      "providerBatchId": "Provider batch ID",
      "status": "Status",
    },
    "show": {
      "cancelBatch": "Cancel batch",
      "meta":
        "{{insurance}} · {{model}} · Provider: {{provider}} · {{sampleCount}} cases · Created by {{creator}} · {{createdAt}}",
    },
    "tabs": {
      "billing": "Billing benchmarks",
      "transcription": "Transcription benchmarks",
    },
    "workspace": {
      "deleteDraft": "Delete draft",
      "f1": "F1",
      "noRunsYet": "No benchmark runs yet.",
    },
  },
  "billingPositions": {
    "addMissing": "Add missing position",
    "addMissingLaboratory": "Add missing laboratory service",
    "addPosition": "Add position",
    "aiConfidence": "AI confidence: {{value}}%",
    "analog": {
      "configuredAmount": "Configured amount: {{amount}}",
      "group": "Analog services",
      "tooltip": "Analog service",
      "tooltipWithDescription": "Analog service: {{description}}",
    },
    "bulkActionFailed": "Not all positions could be updated. Please try again.",
    "card": {
      "commentPreview": "Comment",
      "deleteRegion": "Delete position for region {{region}}",
      "editRegion": "Edit position for region {{region}}",
      "editRegionComment": "Edit comment for region {{region}}",
      "includeRegion": "Add position for region {{region}}",
      "markRegionCorrect": "Mark region {{region}} as correct",
      "markRegionIncorrect": "Mark region {{region}} as incorrect",
      "openPosition": "Open position {{code}}",
      "openRegion": "Open position {{code}} for region {{region}}",
      "regionCommentPreview": "Comment ({{region}})",
      "regionDetails": "Teeth in this region",
      "showLess": "Show less",
      "showMore": "Show more",
    },
    "conflict": {
      "confirm": "Add anyway",
      "description":
        "Position {{position}} has an exclusion conflict with {{conflicts}} in our database.",
      "followup":
        "Do you still want to add it as a missing position? Please add a comment explaining why.",
      "mutualExclusion": "{{a}} and {{b}} exclude each other.",
      "pluralTitle": "Exclusion conflicts detected",
      "title": "Exclusion conflict detected",
    },
    "countTooltip_one": "{{label}}: {{count}} position",
    "countTooltip_other": "{{label}}: {{count}} positions",
    "delete": {
      "description": 'Are you sure you want to delete position "{{code}}"?',
      "descriptionWithRegion_one":
        'Are you sure you want to delete position "{{code}}" for region {{region}}?',
      "descriptionWithRegion_other":
        'Are you sure you want to delete position "{{code}}" for region {{region}}? All {{count}} charges on this card will be removed.',
      "title": "Delete position",
    },
    "drawer": {
      "analogChargedAmount": "Billing amount",
      "analogTitle": "Analog service",
    },
    "edit": {
      "factor": "Factor",
      "justification": "Justification",
      "justificationCatalog": "Predefined justification",
      "justificationCatalogPlaceholder": "Select a justification",
      "justificationPlaceholder": "Justification for factor increase...",
      "justificationRequired":
        "A justification is required when the factor is higher than 2.3.",
      "region": "Teeth / region",
      "regionHint":
        "Enter individual teeth or FDI ranges, e.g. 14, 15 or 15-13.",
      "regionMissing": "Tooth selection missing",
      "regionPerPositionHint":
        "Teeth are kept per position and cannot be changed for the whole card at once.",
      "regionPlaceholder": "e.g. 14, 15 or 15-13",
      "surfaces": "Surfaces",
      "surfacesHint_one": "Count must match the code ({{count}} surface).",
      "surfacesHint_other": "Count must match the code ({{count}} surfaces).",
      "surfacesHintMin_one":
        "Count must match the code (at least {{count}} surface).",
      "surfacesHintMin_other":
        "Count must match the code (at least {{count}} surfaces).",
      "surfacesMissing": "Surfaces missing",
      "surfacesUnitHint":
        "Surfaces describe the cavity or fee tier and are not an independent billable unit.",
      "title": "Edit position: {{code}}",
      "tooth": "Tooth",
      "toothHint": "Enter exactly one FDI tooth.",
      "toothPlaceholder": "e.g. 14",
    },
    "emptyState": {
      "description":
        'AI could not derive any positions from the documentation. Add positions manually via "{{addLabel}}" if needed.',
      "title": "No billing positions",
    },
    "experimentalAlert": {
      "bulletLiability":
        "Dentero assumes no liability for the accuracy of billing suggestions.",
      "bulletReport":
        'Use the "Report" function if the suggestions appear incorrect.',
      "bulletVerify":
        "Review suggestions for plausibility and adjust as needed.",
      "dismiss": "I understand, hide notice.",
      "intro":
        "Billing suggestions are an AI feature currently in testing and development. The AI can and will make mistakes.",
      "title": "Experimental billing suggestions",
    },
    "factorIncrease": "Factor increase:",
    "grandTotal": {
      "label": "Overall total",
      "total": "Overall total {{amount}}",
    },
    "laboratory": {
      "countInstances_one": "{{count}} detected instance",
      "countInstances_other": "{{count}} detected instances",
      "countShort_one": "{{count}} laboratory service",
      "countShort_other": "{{count}} laboratory services",
      "delete": {
        "description":
          'Do you really want to remove the laboratory service "{{code}}"?',
        "title": "Remove laboratory service",
      },
      "drawer": {
        "confidence": "AI confidence",
        "count": "Count",
        "position": "Position",
        "serviceType": "Service type",
        "source": "Source",
        "title": "Laboratory service {{code}}",
      },
      "editCount": "Edit count",
      "eigenlabor": "In-house laboratory",
      "fremdlabor": "External laboratory",
      "source": "This laboratory service was detected automatically.",
      "sourceComplex": "This laboratory service was taken from a complex.",
      "sourceManual": "This laboratory service was added manually.",
      "title": "Laboratory services",
      "total": "Total {{amount}}",
    },
    "materials": {
      "delete": {
        "description": 'Do you really want to remove the material "{{name}}"?',
        "title": "Remove material",
      },
      "drawer": {
        "count": "Count",
        "unitPrice": "Unit price",
        "unitPricePlaceholder": "e.g. 0.67",
      },
      "edit": "Edit material",
      "noPrice": "No price",
      "title": "Materials",
      "total": "Total {{amount}}",
      "unitPrice": "Unit price",
      "untitled": "Material",
    },
    "missingPositionTooltip": "Missing position (added for review)",
    "mkv": {
      "badge": "MKV",
      "payerSplit": {
        "kasse": "Kasse (BEMA)",
        "note":
          "The BEMA filling is included in the price of the GOZ filling and is paid by the Kasse. The patient share is approximate.",
        "patient": "Patient (approx.)",
        "trigger": "Show cost breakdown",
      },
      "tooltip":
        "This GOZ position is a filling or inlay covered by the Mehrkostenregelung and requires a supplementary fee agreement (Mehrkostenvereinbarung) for a statutory (GKV) patient.",
    },
    "previouslyBilled": {
      "badge": "Already billed",
      "tooltip":
        "Already billed for this patient on {{date}}. This position can only be billed again on a limited basis {{window}}.",
      "viewTreatment": "View previous treatment",
      "window": {
        "same_calendar_year": "within a calendar year",
        "same_day": "on the same day",
        "same_half_year": "within a calendar half-year",
        "same_quarter": "within a quarter",
        "within_four_months": "within four months",
        "within_two_calendar_years": "within two calendar years",
      },
    },
    "review": {
      "commentLabel": "Comment",
      "done": "Done",
      "editComment": "Edit comment",
      "incorrectCommentDescription":
        "Briefly explain why this AI suggestion is incorrect.",
      "incorrectCommentPlaceholder": "Why is this suggestion incorrect?",
      "incorrectCommentTitle": "Comment on incorrect position",
      "lowConfidence_one": "Uncertain suggestion ({{count}})",
      "lowConfidence_other": "Uncertain suggestions ({{count}})",
      "markAllCorrect": "Mark all as correct",
      "markCorrect": "Mark as correct",
      "markIncorrect": "Mark as incorrect",
      "missingCommentDescription":
        "Briefly explain why this position is missing from the suggestion.",
      "missingCommentPlaceholder": "Why is this position missing?",
      "missingCommentTitle": "Comment on missing position",
      "modeDescription":
        "Rate AI suggestions with thumbs up or down. Your review is used solely to improve the AI and does not affect your documentation.",
      "modeTitle": "Review mode",
      "noMissingPositions": "No positions missing",
      "positionsEvaluated":
        "{{evaluatedCount}} of {{aiPositionsCount}} positions evaluated",
      "submitReview": "Submit review",
      "unknownPosition": "Unknown position",
      "withoutTitle": "Untitled",
    },
    "services": {
      "titlePrivate": "Private billing (GOZ/GOÄ)",
      "titlePublic": "Public billing (BEMA)",
      "total": "Total {{amount}}",
    },
    "source": {
      "ai": "This position was detected automatically.",
      "complex":
        "This position comes from a complex; the AI rated it against the documentation.",
      "manual": "This position was added manually.",
    },
    "surfaces": {
      "CERVICAL": "Cervical",
      "DISTAL": "Distal",
      "INCISAL": "Incisal",
      "MESIAL": "Mesial",
      "OCCLUSAL": "Occlusal",
      "ORAL": "Oral",
      "VESTIBULAR": "Vestibular",
    },
    "table": {
      "count": "Count",
      "factor": "Factor",
      "position": "Position",
      "region": "Region",
      "titleAndExplanation": "Title and explanation",
    },
    "tabs": {
      "analog": "Analog",
      "laboratory": "Laboratory",
      "materials": "Materials",
      "positions": "GOÄ/GOZ",
      "positionsPrivate": "GOZ/GOÄ",
      "positionsPublic": "BEMA/GOZ/GOÄ",
    },
    "truncatedResults":
      "{{shown}} of {{total}} matches – please narrow your search.",
  },
  "billingReviews": {
    "categories": {
      "billing": "Billing",
      "laboratory": "Laboratory",
    },
    "deletedUser": "Deleted user",
    "empty": "No reviews yet.",
    "emptyBilling": "No billing reviews yet.",
    "emptyLaboratory": "No laboratory reviews yet.",
    "export": "Export",
    "filters": {
      "allInsurance": "All",
    },
    "metrics": {
      "f1": {
        "description": "Overall AI quality",
        "footer": "Combines precision and recall into an overall score",
      },
      "precision": {
        "description": "How often the AI is correct",
        "footer": "Of all AI suggestions: how many were correct",
      },
      "recall": {
        "description": "How much the AI finds",
        "footer": "Of all required positions: how many the AI found",
      },
    },
    "noResults": "No reviews found with these filters.",
    "resetFilters": "Reset filters",
    "searchPlaceholder": "Search by patient, practice, or reviewer",
    "show": {
      "evalTable": {
        "code": "Code",
        "comment": "Comment",
        "status": "Status",
        "targetTypes": {
          "analog": "Analog",
          "laboratory": "Laboratory",
        },
        "title": "Title",
        "type": "Type",
      },
      "reviewerComment": "Reviewer comment",
      "viewRecording": "View recording",
    },
    "statusBadges": {
      "correct": "OK",
      "incorrect": "Errors",
      "missing": "Missing",
    },
    "table": {
      "actions": "Actions",
      "category": "Category",
      "createdAt": "Created",
      "insurance": "Insurance",
      "practice": "Practice",
      "ratings": "Ratings",
      "recordingId": "Recording ID",
      "reviewer": "Reviewer",
      "reviewId": "Review ID",
    },
  },
  "chatReport": {
    "alreadyReported": "A report was already sent on {{at}}.",
    "description":
      "Describe what was wrong with the answers. The Dentero team will look at the chat.",
    "sent": "Chat reported",
    "submit": "Submit report",
    "title": "Report chat",
    "trigger": "Report",
  },
  "chats": {
    "index": {
      "columns": {
        "cost": "Cost",
        "lastActivity": "Last activity",
        "messages": "Messages",
        "practice": "Practice",
        "recording": "Recording",
        "user": "User",
      },
      "empty": "No chats yet.",
      "openRecording": "Open recording",
      "searchPlaceholder": "Search messages …",
    },
    "show": {
      "cost": "Cost: {{cost}}",
      "empty": "This conversation has no messages.",
      "lastActivity": "Last activity: {{date}}",
      "messages_one": "{{count}} message",
      "messages_other": "{{count}} messages",
      "openRecording": "Open recording",
    },
  },
  "checkout": {
    "practiceSuccess": {
      "description":
        "The Dentero subscription for {{practiceName}} was completed successfully.",
      "readyDescription":
        "You can sign in with your existing account and use Dentero as usual.",
      "readyTitle": "Dentero is ready",
      "title": "Subscription activated",
    },
    "success": {
      "emailDescription":
        "We sent you an email to set your password and activate your account.",
      "emailHeading": "Check your inbox",
      "loginLink": "Go to sign in",
      "subtitle": "Your registration was successful.",
      "title": "Welcome to Dentero!",
    },
  },
  "combobox": {
    "clearSelection": "Clear selection",
    "create": 'Create "{{label}}"',
    "empty": "Nothing found.",
    "placeholder": "Select...",
    "search": "Search...",
  },
  "common": {
    "accessibility": {
      "breadcrumb": "Breadcrumb",
      "carousel": "Carousel",
      "more": "More",
      "nextSlide": "Next slide",
      "previousSlide": "Previous slide",
      "slide": "Slide",
      "toggleSidebar": "Toggle sidebar",
    },
    "actions": {
      "activate": "Activate",
      "add": "Add",
      "apply": "Apply",
      "back": "Back",
      "cancel": "Cancel",
      "close": "Close",
      "copy": "Copy",
      "deactivate": "Deactivate",
      "delete": "Delete",
      "download": "Download",
      "edit": "Edit",
      "install": "Install",
      "later": "Later",
      "login": "Sign in",
      "remove": "Remove",
      "reset": "Reset",
      "resetPassword": "Reset password",
      "save": "Save",
      "updatePassword": "Change password",
    },
    "answers": {
      "no": "No",
      "yes": "Yes",
    },
    "appName": "Dentero",
    "brands": {
      "dentero": "Dentero",
      "dzr": "Dentero DZR Edition",
    },
    "filters": {
      "all": "All",
    },
    "labels": {
      "currentPassword": "Current password",
      "email": "Email address",
      "firstName": "First name",
      "lastName": "Last name",
      "password": "Password",
      "passwordConfirmation": "Confirm password",
      "patientId": "Patient ID",
      "practice": "Practice",
      "recordedAt": "Recorded at",
      "recordedBy": "Recorded by",
      "role": "Role",
      "title": "Title",
      "treatmentDate": "Treatment date",
      "username": "Username",
    },
    "pagination": {
      "ariaLabel": "Pagination",
      "goToFirstPage": "Go to first page",
      "goToLastPage": "Go to last page",
      "goToNextPage": "Go to next page",
      "goToPreviousPage": "Go to previous page",
      "next": "Next",
      "pageOf": "Page {{page}} of {{last}}",
      "previous": "Previous",
    },
    "states": {
      "active": "Active",
      "admin": "Admin",
      "adminOnlyHint": "Visible to admins only",
      "archived": "Archived",
      "details": "Details",
      "inactive": "Inactive",
      "loading": "Loading...",
      "new": "New",
      "noPractice": "No practice",
      "unknown": "Unknown",
    },
    "themes": {
      "dentero": "Dentero",
      "dzr": "Dentero DZR Edition",
    },
  },
  "community": {
    "join": {
      "cancel": "Cancel",
      "consent":
        "I agree that Dentero creates a forum account for me with my email address {{email}}. Other members cannot see my email address.",
      "description":
        "We create an account for you in our forum. You sign in there with your Dentero account.",
      "submit": "Create account",
      "title": "Join the Dentero Community",
      "usernameHint":
        "Visible to other members. 3 to 20 characters: letters, digits, dot, hyphen and underscore. Don't use your real name if you want to stay anonymous.",
      "usernameInvalid":
        "Use 3 to 20 letters, digits, dots, hyphens or underscores. End with a letter or digit, and don't start with or double a dot or hyphen.",
      "usernameLabel": "Forum name",
    },
    "joined": {
      "description":
        "Your forum name is {{username}}. The first time you open it, the forum sends you an email to confirm your address. If the name was taken, the forum adds a number.",
      "open": "Open community",
      "title": "Your forum account is ready",
    },
  },
  "complexes": {
    "ai": {
      "contentLabel": "Content",
      "contentPlaceholder": "Content for this complex ...",
      "createWithAi": "Create with AI",
      "improveContent": "Improve content",
      "improveWithAi": "Improve with AI",
      "improving": "AI is improving content ...",
      "missingTriggers": "Enter trigger words to generate content.",
      "moreActions": "More AI actions",
      "prefilling": "AI is creating content ...",
      "recreate": "Recreate ...",
      "recreateConfirm": "Recreate",
      "recreateDescription":
        "Existing content will be completely overwritten. Continue?",
      "recreateTitle": "Recreate content",
    },
    "badges": {
      "archived": "(archived)",
      "suggestOnly": "Suggest only",
    },
    "billingPositionEdit": {
      "countNotRepeatable":
        "This position can be billed only once per treatment or per tooth, so a complex holds it once.",
      "factorPlaceholder": "e.g. 2.3",
      "freeTextJustificationsLabel": "Free-text justifications (one per line)",
      "loadingJustifications": "Loading justifications...",
      "noPredefinedJustifications": "No predefined justifications available.",
      "predefinedJustificationsLabel": "Predefined justifications",
    },
    "billingSection": {
      "emptyTitle": "No positions configured",
      "exclusionWarningItem":
        "{{a}} and {{b}} are usually not billed at the same time.",
      "exclusionWarningTitle": "Possible exclusion note",
      "intro":
        "Positions are added to the billing suggestions when the complex is applied.",
    },
    "checklist": {
      "actions": "Actions for {{name}}",
      "add": "Add complex",
      "addEmpty": "No other complexes found.",
      "addSearch": "Search complexes",
      "appliedCount_one": "{{count}} applied",
      "appliedCount_other": "{{count}} applied",
      "apply": "Apply",
      "applying": "being applied …",
      "complete": "complete",
      "detected": "Detected: “{{reason}}” · confidence {{confidence}}%",
      "detectedNoReason": "Confidence {{confidence}}%",
      "detecting": "Detecting complexes …",
      "editInSettings": "Edit complex in settings",
      "inStructured": "Structured doc",
      "kindInText": "in text",
      "kindReport": "Report",
      "kindTemplate": "Template",
      "merged": "worked in",
      "openSlots_one": "{{count}} open",
      "openSlots_other": "{{count}} open",
      "pendingRemoval": "removed when documentation is recreated",
      "refill": "Refill template",
      "refillReport": "Rewrite report",
      "remove": "Remove complex",
      "removed": "removed",
      "report": "written as report",
      "restore": "Restore",
      "suggestion": "Suggestion",
      "suggestionCount_one": "{{count}} suggestion",
      "suggestionCount_other": "{{count}} suggestions",
    },
    "dialog": {
      "archivedSuffix": " (no longer applied)",
      "description": "Documentation complex content",
      "loading": "Loading content...",
    },
    "edit": {
      "autoApply": "Apply automatically",
      "autoApplyHint":
        "When off, the complex stays a suggestion you apply with one click.",
      "billingTabPrefix": "Billing",
      "detectionTitle": "Detection",
      "editTrigger": "Edit “{{trigger}}”",
      "emptyContent": "No content available.",
      "formErrorTitle": "Complex could not be saved",
      "includeInStructured": "Include in structured documentation",
      "includeInStructuredHint":
        "The AI files the content of the filled template into the matching fields of the structured documentation.",
      "mergeIntoDocumentation": "Merge into documentation",
      "mergeIntoDocumentationHint":
        "When off, the report appears as its own section below the documentation instead of being merged into the improved version. Applies to new recordings.",
      "promptTab": "Prompt",
      "removeTrigger": "Remove “{{trigger}}”",
      "tabHasErrors": "Has errors",
      "triggerWordsHint":
        "Use specific clinical terms, not everyday words. Separate with Enter or a comma. Click a term to edit it.",
      "triggerWordsPlaceholder": "e.g. extraction, PSI",
      "unsavedActionMessage": "There are unsaved changes.",
    },
    "form": {
      "continue": "Continue",
      "description":
        "Use specific terms as trigger words (avoid common everyday words). Avoid abbreviations. More context is better. Separate multiple triggers with Enter or a comma.",
      "newTitle": "Create new complex",
      "prefillWithAi": "Suggest matching content with AI",
      "triggerWordsLabel": "Trigger words",
      "triggerWordsPlaceholder": "e.g. extraction, PSI",
    },
    "index": {
      "activateDescription": "Do you want to reactivate this archived complex?",
      "activateTitle": "Activate complex",
      "add": "Add",
      "archiveAction": "Archive",
      "archiveDescription":
        "Do you want to archive this complex? Archived complexes are no longer applied.",
      "archiveTitle": "Archive complex",
      "askAssistant": "Change with the setup assistant",
      "clearSearch": "Clear search",
      "deleteAction": "Delete permanently",
      "deleteDescription":
        "Do you want to permanently delete this complex? This action cannot be undone. Complexes already applied in existing treatments will be retained but will no longer be displayed.",
      "deleteTitle": "Permanently delete complex",
      "description":
        "Complexes are pre-built documentation fragments that can be triggered by trigger words and automatically integrated into documentation.",
      "empty": "No complexes yet.",
      "learnMore": "Learn more",
      "noSearchResults": "No complexes found.",
      "searchPlaceholder": "Search trigger words",
    },
    "row": {
      "description":
        "Checked complexes are applied. Expand a row to read the text or fill a template. Suggestions were detected but not added, for example because the treatment was only discussed.",
      "help": "What do the complexes mean?",
      "pendingRemoval_one":
        "{{count}} complex will be removed when documentation is recreated.",
      "pendingRemoval_other":
        "{{count}} complexes will be removed when documentation is recreated.",
      "reprocess": "Recreate documentation",
      "reprocessNeeded":
        "The changed setting takes effect once you recreate the documentation.",
      "statusDiscussed": "only discussed",
      "statusMentioned": "only mentioned",
      "statusTriggerMatch": "trigger word in transcript",
      "title": "Complexes",
      "useForRecording": "Use complexes for this recording",
    },
    "selection": {
      "apply": "Apply",
      "statusExcluded": "not applied",
      "statusPendingRemoval": "will be removed on next reprocess",
      "title": "Complexes",
      "toggleAriaLabel": "Apply complex “{{name}}”",
      "viewAriaLabel": "View complex “{{name}}”",
    },
    "template": {
      "badge_one": "Exact structure · {{count}} placeholder",
      "badge_other": "Exact structure · {{count}} placeholders",
      "collapseRepeatedLines": "Merge",
      "convertConditional": "Convert",
      "detectedSlots_one": "{{count}} placeholder detected",
      "detectedSlots_other": "{{count}} placeholders detected",
      "detectSlots": "Detect placeholders and conditions",
      "insertSlot": "Placeholder",
      "kinds": {
        "choice": "Choice between alternatives",
        "condition":
          "Line only appears if the documentation says the condition holds",
        "fill": "Value the AI fills in based on the description",
        "list": "Bullet points: each point appears on its own line with “-”",
        "repeat":
          "Line is repeated per occurrence, e.g. per tooth. Mid-line, the rest of the line repeats on the same line, separated by commas",
      },
      "prompt": {
        "hint":
          "Appended after the practice prompt when the placeholders are filled. Templates without placeholders don't use it.",
        "label": "AI instruction for this template",
        "placeholder": "e.g. always give teeth in FDI notation",
        "reportHint":
          "Only used while the complex has no content. The AI then writes this part of the documentation from the instruction, for example an OPG report. Once the complex has content, the content is worked in and the instruction is ignored.",
        "reportLabel": "AI instruction for this complex",
        "test": {
          "description":
            "Fills the current template with the AI instruction from the form, without saving. The text is sent without physician, date, and original transcript.",
          "inputLabel": "Documentation text",
          "missingSlots_one": "{{count}} placeholder open:",
          "missingSlots_other": "{{count}} placeholders open:",
          "requestFailed": "Test failed.",
          "result": "Result",
          "submit": "Run test",
          "title": "Test template",
          "trigger": "Test without saving",
        },
      },
      "slotDialog": {
        "addOption": "Add alternative",
        "choiceLabel": "Alternatives",
        "choicePlaceholder": "e.g. infiltration",
        "conditionLabel": "When should the line appear?",
        "conditionPlaceholder": "e.g. OPG taken",
        "description":
          "A value is filled from the documentation. A choice keeps only the matching alternative. A repeat emits the line once per occurrence. A condition only shows the line if the documentation says it holds.",
        "editTitle": "Edit placeholder",
        "fillLabel": "What should be filled in?",
        "fillPlaceholder": "e.g. tooth",
        "insert": "Insert",
        "kinds": {
          "choice": "Choice",
          "condition": "Condition",
          "fill": "Value",
          "repeat": "Repeat",
        },
        "repeatLabel": "What is it repeated for?",
        "repeatPlaceholder": "e.g. tooth",
        "title": "Insert placeholder",
      },
      "strictStructureHint":
        "The template is reproduced line by line. The AI only fills placeholders in curly braces and picks alternatives from the documentation. Lines starting with {pro Zahn} are repeated per tooth. With {pro Zahn} mid-line, the rest of the line repeats per tooth on the same line. Lines starting with {wenn …} only appear if the documentation says the condition holds. Unfilled placeholders stay visible.",
      "strictStructureInfo": "What does exact structure mean?",
      "strictStructureLabel": "Keep exact structure",
      "warningLinePrefix": "Line {{line}}: ",
      "warnings": {
        "braceTooLong":
          "{{raw}} is too long, a placeholder has at most {{max}} characters.",
        "choiceNeedsTwoOptions":
          "{{raw}} needs at least two alternatives and is used as text.",
        "conditionalLikeFill":
          "{{raw}} looks like a condition. Start the line with {wenn …} so it only appears if the condition holds.",
        "conditionBlockStrayEnd":
          "{{raw}} does not close a section and is read as a placeholder.",
        "conditionBlockUnclosed":
          "{{raw}} has no matching {ende} line. Without it the condition is read as a placeholder.",
        "conditionEmpty":
          "{{raw}} names no condition and is read as a placeholder.",
        "conditionInRepeatHeader":
          "{{raw}} cannot be combined with {pro …} and is read as a placeholder.",
        "conditionNeedsContent":
          "No text follows {{raw}}. The condition is read as a placeholder.",
        "conditionNested":
          "{{raw}} is inside a {wenn …} section. Conditions cannot be nested. Close the section with {ende} first, otherwise the complex cannot be saved.",
        "conditionNestedSlot":
          "{{raw}} has a placeholder after “dann”. Start the line with {wenn …} instead.",
        "conditionNotAtLineStart":
          "{{raw}} only applies to the line at the line start. Otherwise add “dann …”. Until then it is read as a placeholder.",
        "conditionThenEmpty":
          "{{raw}} has no text after “dann” or “sonst” and is read as a placeholder.",
        "contentTooLong": "{{total}} characters, at most {{max}} are allowed.",
        "emptyBraces": "{{raw}} is empty and is used as text.",
        "numericChoice": "{{raw}} is all numbers and is read as a choice.",
        "repeatedLines":
          "Lines {{from}} to {{to}} are identical. Start the line with {{header}} to repeat it per occurrence.",
        "repeatNeedsPlaceholder":
          "{{raw}} repeats a line that has no placeholder.",
        "tooManySlots": "{{total}} placeholders, at most {{max}} are allowed.",
      },
    },
  },
  "confirmation": {
    "description": "This action cannot be undone.",
    "title": "Are you sure?",
  },
  "connector": {
    "dialog": {
      "channels": {
        "beta": "Beta",
        "stable": "Release",
      },
      "createToken": "Create Connector token",
      "description":
        "Install the Dentero Connector on the Windows PC that runs your practice software.",
      "download": "Download",
      "title": "Dentero Connector",
      "tokenCreatedHint":
        "Copy it now and paste it into the Connector during setup. It can't be shown again.",
      "tokenCreatedTitle": "Connector token created",
      "tokenExists":
        "A Connector token ending in {{lastFour}} already exists (created {{created}}). It can't be shown again. If you lost it, contact Dentero support.",
      "tokenFailed": "Connector token could not be created.",
      "tokenLoading": "Checking for an existing token…",
      "tokenMissing":
        "The Connector needs an API token to connect to your practice.",
      "tokenTitle": "API token",
      "userPlaceholder": "Select a practice member",
      "version": "Version {{version}}",
    },
  },
  "customization": {
    "acceptSuggestion": "Use suggestion",
    "acceptSuggestionKey": "Tab",
    "actions": {
      "archive": "Archive",
      "create": "New",
      "delete": "Delete",
      "update": "Change",
    },
    "areas": {
      "assistant": "Documentation assistant",
      "complex": "Complex",
      "custom_prompt": "Custom prompt",
      "default_factor": "Default factor",
      "practice": "Practice settings",
    },
    "blockers": {
      "dry_run_required":
        "A successful test run is needed before applying. Ask the assistant to run it.",
      "screening_failed":
        "The safety check rejected this prompt. It cannot be applied.",
    },
    "buttons": {
      "apply": "Apply",
      "discard": "Discard",
      "open": "Open",
      "undo": "Undo",
    },
    "clearHistory": {
      "button": "Delete chat history",
      "confirm": "Delete history",
      "description":
        "This deletes every user's chat in this practice, with all messages and proposals. Applied changes stay in the configuration and the audit trail.",
      "title": "Delete the practice's chat history?",
    },
    "description":
      "Describe what Dentero should do differently in your practice. The assistant proposes matching changes. Only what you apply is saved.",
    "disclaimer":
      "The assistant can be wrong. Check every change before you apply it.",
    "dryRun": {
      "failed": "Test run without result",
      "input": "Sample input",
      "output": "Result",
      "passed": "Test run succeeded",
    },
    "empty": {
      "description":
        "Pick a suggestion or describe what you want in your own words.",
      "title": "What should Dentero do differently in your practice?",
    },
    "errors": {
      "generic": "That did not work. Please try again.",
    },
    "fields": {
      "assistant": {
        "classification": "Classification",
        "content": "Guidance",
        "title": "Title",
      },
      "complex": {
        "auto_apply": "Apply automatically",
        "content": "Content",
        "trigger_words": "Trigger words",
      },
      "custom_prompt": {
        "class_name": "AI step",
        "content": "Instructions",
      },
      "default_factor": {
        "factor": "Factor",
        "justification": "Justification",
        "regulation_position_id": "Position",
      },
      "practice": {
        "additional_address": "Address line 2",
        "address": "Address",
        "city": "City",
        "complexes_applied_by_default": "Apply complexes by default",
        "documentation_assistant_enabled_by_default":
          "Documentation assistant on by default",
        "documentation_download_format": "Download format",
        "email": "Email",
        "kzv": "KZV",
        "name": "Practice name",
        "protect_privacy": "Privacy mode",
        "realtime_transcription_enabled_by_default":
          "Live transcription on by default",
        "retention_period_audio": "Audio retention",
        "skip_ai_requests": "Skip AI processing",
        "zip": "ZIP",
      },
    },
    "focus": {
      "clear": "Remove reference",
      "complex": "Complex “{{name}}”",
      "placeholder": "What should change about complex “{{name}}”?",
    },
    "oversight": {
      "appliedChanges": "Applied changes",
      "auditTrail": "Audit trail",
      "empty": "No setup chats yet.",
      "searchPlaceholder": "Search messages",
    },
    "placeholder": "Describe what Dentero should do differently",
    "screening": {
      "failed": "Safety check failed",
      "findings": {
        "blank": "The instructions are empty.",
        "markdown_image": "Contains an image.",
        "markup_tag": "Contains markup or tags.",
        "override_instructions": "Asks to ignore rules or instructions.",
        "reveal_data": "Asks to output data in full or verbatim.",
        "role_marker": "Contains role markers such as “System:”.",
        "template_syntax": "Contains template syntax.",
        "too_long": "The instructions are too long.",
        "url": "Contains a link or web address.",
      },
      "passed": "Safety check passed",
    },
    "send": "Send",
    "status": {
      "applied": "Applied",
      "discarded": "Discarded",
      "pending": "Proposal",
      "undone": "Undone",
    },
    "suggestions": {
      "assistant": "Adjust the documentation assistant for endodontics",
      "complex": "Create or change a complex",
      "custom_prompt": "Make the documentation shorter",
      "default_factor": "Set a default factor for a GOZ position",
      "practice": "Change practice settings (e.g. download as PDF)",
    },
    "title": "Setup assistant",
    "toolCalls_one": "{{count}} call made",
    "toolCalls_other": "{{count}} calls made",
    "tools": {
      "draft_complex_content": "Drafted content",
      "list_assistants": "Read assistants",
      "list_complexes": "Read complexes",
      "list_custom_prompts": "Read custom prompts",
      "list_default_factors": "Read default factors",
      "propose_assistant_delete": "Proposed deletion",
      "propose_assistant_override": "Proposed assistant",
      "propose_assistant_update": "Proposed assistant change",
      "propose_complex_archive": "Proposed archiving",
      "propose_complex_create": "Proposed complex",
      "propose_complex_update": "Proposed complex change",
      "propose_custom_assistant": "Proposed assistant",
      "propose_custom_prompt_update": "Proposed prompt",
      "propose_default_factor_delete": "Proposed deletion",
      "propose_default_factor_upsert": "Proposed default factor",
      "propose_practice_profile_update": "Proposed profile change",
      "propose_practice_setting": "Proposed setting",
      "search_regulation_position": "Searched positions",
      "show_complex": "Read complex",
      "show_practice_settings": "Read settings",
      "suggest_reply": "Suggested a reply",
      "test_custom_prompt": "Test run",
    },
    "values": {
      "empty": "(empty)",
      "no": "No",
      "yes": "Yes",
    },
    "warnings": {
      "custom_prompt":
        "Custom prompts affect every recording of your practice. Check the test run carefully.",
      "practice": {
        "protect_privacy":
          "Privacy mode decides who can see audio and transcripts.",
        "retention_period_audio":
          "The retention period decides when audio files are deleted for good.",
        "skip_ai_requests":
          "Without AI processing there is no documentation and no billing suggestions.",
      },
    },
  },
  "customPrompts": {
    "aiRequestTypes": {
      "gkvSuggestion": "GKV suggestion",
      "improvedDocumentation": "Improved version",
      "patientBriefing": "Patient briefing",
      "pkvSuggestion": "PKV suggestion",
      "structuredDocumentation": "Structured documentation",
    },
    "edit": {
      "insertDefaultTemplate": "Insert default template",
      "promptPlaceholder": "Customization for this prompt...",
      "promptTab": "Prompt",
      "responseFieldsTab": "Additional response fields",
      "templateHint": "Leave empty to use the default template.",
      "templatePlaceholder": "Liquid template for output...",
      "templateTab": "Template (Liquid)",
    },
    "form": {
      "description": "Select the prompt type you want to customize.",
      "newTitle": "New custom prompt",
      "typePlaceholder": "Select type...",
    },
    "index": {
      "add": "Add",
      "breadcrumb": "Custom prompts",
      "deleteDescription":
        "Are you sure you want to delete this custom prompt? This action cannot be undone.",
      "deleteTitle": "Delete custom prompt",
      "empty": "No custom prompts yet.",
      "noChanges": "No customizations yet.",
      "promptCustomized": "Prompt customized",
      "responseFieldsCustomized": "Response fields customized",
      "templateCustomized": "Template customized",
      "usingDefaultPrompt": "Prompt not customized — default prompt is used.",
    },
    "responseFields": {
      "add": "Add field",
      "defaultFieldsDescription":
        "Dentero requires these fields, so they cannot be changed.",
      "defaultFieldsTitle": "Default fields",
      "descriptionHint":
        "The description is sent to the model and shown in template autocomplete.",
      "descriptionLabel": "Description",
      "descriptionPlaceholder":
        "What information should the model return in this field?",
      "errors": {
        "descriptionRequired": "A description is required.",
        "descriptionTooLong": "A description is too long.",
        "jsonInvalid": "The JSON is invalid.",
        "jsonShape":
          "The JSON must be an array of objects with key, type, description, and optional nullable.",
        "keyDuplicate": "This key is already in use.",
        "keyFormat":
          "Use only lowercase letters, numbers, and underscores. The key must start with a letter or underscore.",
        "keyRequired": "A key is required.",
        "keyReserved": "This key is reserved for a default field.",
        "tooLarge":
          "The additional fields are too large overall. Shorten their descriptions or remove fields.",
        "typeUnsupported": "A field uses an unsupported type.",
      },
      "jsonHint":
        "Paste a JSON array. Each object needs key, type, and description. nullable is optional and defaults to true.",
      "keyLabel": "Key",
      "keyPlaceholder": "e.g. special_observations",
      "limit_one": "At most {{count}} additional field can be added.",
      "limit_other": "At most {{count}} additional fields can be added.",
      "modeForm": "Form",
      "modeJson": "JSON",
      "nullableHint":
        "Allows null when the documentation contains no matching information.",
      "nullableLabel": "Allow an empty value",
      "promptPreviewDescription":
        "These field instructions are appended to the prompt automatically.",
      "promptPreviewTitle": "Automatically generated prompt instructions",
      "remove": "Remove field",
      "typeLabel": "Type",
      "warnings": {
        "notUsedInTemplate":
          "This field is not used in the current template, so it will not appear in the documentation.",
      },
    },
    "test": {
      "description":
        "Test the customization for this prompt with sample documentation.",
      "invalidJson": "Invalid JSON:",
      "jsonLabel": "JSON",
      "pending": "Testing...",
      "requestFailed": "Prompt test failed",
      "response": "Response",
      "result": "Result",
      "submit": "Test",
      "title": "Test prompt",
      "trigger": "Test prompt",
    },
  },
  "dashboard": {
    "charts": {
      "byStatus": {
        "description": "Processed vs. pending",
        "title": "By status",
      },
      "byTreater": {
        "description": "Recordings per user",
        "title": "By treater",
      },
      "recordingsByDay": {
        "title": "Recordings per day",
      },
    },
    "documentationPool": {
      "exhausted": {
        "body": "Nothing is blocked. Customers do not see this.",
        "title": "The documentation pool is used up",
      },
      "period": "{{start}} to {{end}}",
      "sharedBy": "Every practice on the subscription shares this pool:",
      "title": "Documentation pool",
      "usedOfLimit": "{{used}} of {{limit}} documentations used",
      "youUsed_one": "You created {{count}} of these",
      "youUsed_other": "You created {{count}} of these",
    },
    "emptyState": {
      "noData": "No data",
    },
    "filters": {
      "allPractices": "All practices",
      "custom": "Custom...",
      "period": "Period",
    },
    "stats": {
      "documentationUsage": {
        "thisMonth": "This month",
        "title": "Documentations",
        "usedOfLimit": "{{used}} / {{limit}}",
        "youUsed_one": "You created {{count}}",
        "youUsed_other": "You created {{count}}",
      },
      "medianLength": {
        "description": "Per recording",
        "footer": "Median recording duration",
        "title": "Median length",
      },
      "recordings": {
        "footer": "Total number of recordings",
        "title": "Recordings",
      },
      "selectedPeriod": "In selected period",
      "totalDuration": {
        "description": "All recordings",
        "footer": "Total recorded time",
        "title": "Total duration",
      },
      "treaters": {
        "footer": "Total number of treaters",
        "title": "Treaters",
      },
    },
  },
  "dentalChart": {
    "attributes": {
      "acuteApicalAbscess": "acute apical abscess",
      "apicalPeriodontitis": "apical periodontitis",
      "asymptomaticApicalPeriodontitis": "asymptomatic apical periodontitis",
      "bridgeAbutment": "bridge abutment",
      "chronicApicalAbscess": "chronic apical abscess",
      "clasp": "clasp/retainer",
      "condensingOsteitis": "condensing osteitis",
      "cyst": "cyst",
      "irreversiblePulpitis": "irreversible pulpitis",
      "nonVital": "non-vital",
      "percussionSensitive": "percussion-sensitive",
      "post": "post and core",
      "reversiblePulpitis": "reversible pulpitis",
      "rootCanalTreated": "root-canal treated",
      "splinted": "splinted",
      "superstructure": "superstructure",
      "symptomaticApicalPeriodontitis": "symptomatic apical periodontitis",
      "wisdomToothRemovalIndicated": "removal indicated",
    },
    "changes": {
      "appliedTitle": "Dental chart updated",
      "closeReview": "Hide chart",
      "empty": "No chart changes from this recording.",
      "emptyEditable":
        "No changes detected. You can still edit the chart here.",
      "openLiveChart": "Open current tooth status",
      "openReview": "Show chart",
      "pending": "The tooth status for this recording is still being prepared.",
      "queued_one": "{{count}} more chart update is still being generated.",
      "queued_other": "{{count}} more chart updates are still being generated.",
      "working": "Updating the tooth status …",
    },
    "chart": {
      "aiBadge": "AI",
      "letterAria": {
        "b": "{{number}} pontic",
        "bPlanned": "{{number}} pontic, planned",
        "f": "{{number}} missing",
      },
      "showCounterpart": "Show other tooth {{number}}",
      "statusAria": "{{number}}, {{status}}",
      "statusPlannedAria": "{{number}}, {{status}}, planned",
    },
    "chartAria": "Odontogram",
    "editor": {
      "addFinding": "Add finding",
      "apply": "Save",
      "attributeGroups": {
        "endodontic": "Endodontic",
        "other": "Other",
        "prosthetic": "Prosthetic",
      },
      "cancel": "Cancel",
      "chooseFinding": "Choose finding",
      "dentition": "Dentition",
      "failed": "The change could not be saved.",
      "findingLabel": "Finding {{index}}",
      "findingTaken": "Already on this surface",
      "findingType": "Finding",
      "kzbvCode": "KZBV code",
      "kzbvPlaceholder": "e.g. kw",
      "liveChartHint":
        "Edits update the current tooth status, not only this visit.",
      "material": "Material",
      "maxFindings": "A surface can have at most {{count}} findings.",
      "maxFindings_one": "A surface can have at most {{count}} finding.",
      "maxFindings_other": "A surface can have at most {{count}} findings.",
      "noFindings": "No finding on {{number}} {{surface}}.",
      "notGraded": "Ungraded",
      "pathology": "Pathology",
      "permanent": "permanent ({{number}})",
      "primary": "primary ({{number}})",
      "radiographicDepth": "Radiographic depth",
      "recurrent": "recurrent (CARS)",
      "removeFinding": "Remove",
      "restoration": "Restoration",
      "severity": "ICDAS grade",
      "state": "State",
      "status": "Status",
      "surfaceFindings": "Surface findings",
      "title": "Tooth {{number}}",
    },
    "findings": {
      "abrasion": "Abrasion",
      "attrition": "Attrition",
      "caries": "Caries",
      "discoloration": "Discoloration",
      "erosion": "Erosion",
      "filling": "Filling",
      "fracture": "Fracture",
      "hypoplasia": "Enamel hypoplasia",
      "insufficient-restoration": "Insufficient restoration",
      "sealant": "Sealant",
      "wedge-defect": "Wedge-shaped defect",
    },
    "legend": {
      "badges":
        "Codes: WF root canal, St post, BA bridge abutment, SK superstructure, Vb splinted, −V non-vital, AP apical periodontitis, Zy cyst",
      "bridge": "Bridge across abutments and pontic",
      "cervicalDot": "Dot at the neck: cervical finding",
      "changed": {
        "patient": "Changed since previous version",
        "recording": "Changed in this visit",
      },
      "crownDefective": "crown needs renewal",
      "crownDot": "Dot on the crown: discoloration or hypoplasia",
      "crownPlanned": "crown planned",
      "extractionPlanned": "✕ extraction planned",
      "findingsTitle": "Findings",
      "gapClosed": "gap closed )(",
      "gapClosedDistal": "closed distally )",
      "gapClosedMesial": "closed mesially (",
      "impacted": "Tooth under the gum: impacted (KZBV code re)",
      "implant": "Screw symbol: implant",
      "implantPlanned": "implant planned",
      "indirectFilling": "Indirect filling (ceramic, gold, metal)",
      "innerBand": "Band below the crown: lingual or palatal finding",
      "missing": "missing",
      "pontic": "pontic",
      "ponticPlanned": "pontic planned",
      "prosthetic": "Crown / prosthetic",
      "replaced": "replaced (e)",
      "splinted": "splinted",
      "stateTitle": "State",
      "title": "Legend",
      "toothTitle": "Tooth",
      "unerupted": "Tooth under the gum: unerupted",
      "workPlanned": "filling, sealant, root filling planned",
    },
    "live": {
      "handedOver":
        "The chart is rebuilt from the dictation for the saved patient number and appears in a moment.",
      "phase": {
        "fallback": "live unavailable · arrives after the recording",
      },
      "readOnly": "view only while recording",
      "saved":
        "Chart saved — the chart you watched while dictating is now the chart on the patient.",
      "stale":
        "The chart changed in the meantime. It is being recalculated and will appear in a moment.",
      "waiting": "No tooth heard yet. Dictate a tooth number.",
    },
    "materials": {
      "amalgam": "amalgam",
      "ceramic": "ceramic",
      "compomer": "compomer",
      "composite": "composite",
      "glass-ionomer": "glass ionomer",
      "gold": "gold",
      "metal": "metal",
      "other": "other",
      "temporary": "temporary",
    },
    "origin": {
      "before": "Before",
      "current": "Current finding",
      "edit": "Edit",
      "noHistory": "No history for this tooth.",
      "notCharted": "not charted",
      "openRecording": "Open recording",
      "showInTranscript": "Show in dictation",
      "title": "Tooth {{number}}",
      "treatedAt": "Treated",
      "unexamined": "Not charted",
    },
    "quickCard": {
      "addFindingTitle": "Set finding · {{surface}}",
      "aiHint": "AI · this recording",
      "emptyFindings": "No findings on this tooth.",
      "findingCount_one": "{{count}} finding",
      "findingCount_other": "{{count}} findings",
      "findingsBlocked":
        "This tooth ({{status}}) cannot carry surface findings. Change the tooth status to chart findings.",
      "findingsBlockedAction": "Change tooth status",
      "findingsNone": "no findings",
      "nextTooth": "Next tooth",
      "prevTooth": "Previous tooth",
      "savedHint": "Saved instantly",
      "surfaceCross": "Surface picker",
      "tabFindings": "Findings",
      "tabTooth": "Tooth status",
    },
    "review": {
      "editHint": "Tap a tooth to correct it.",
      "frozenNotice":
        "The tooth status has since changed and can no longer be edited here.",
      "noChartAtVisit":
        "There was no tooth status yet at the time of this treatment.",
      "notInTranscript": "Tooth {{number}} was not found in the transcription.",
      "transcriptAria": "Transcription with highlighted tooth mentions",
      "transcriptEmpty":
        "No transcription is available for this recording yet.",
      "transcriptToggle": "Transcription",
    },
    "stage": {
      "announce": "Tooth {{number}}: {{what}}",
      "announceFailed": "Tooth {{numbers}} not charted",
      "announceRemoved": "{{what}} removed",
      "crossAria": "Surfaces of tooth {{number}}",
      "idleHint": 'for example "16 occlusal caries"',
      "idleTitle": "Say a tooth number",
      "jaw": {
        "lowerLeft": "Lower left",
        "lowerRight": "Lower right",
        "upperLeft": "Upper left",
        "upperRight": "Upper right",
      },
      "kind": {
        "canine": "Canine",
        "incisor": "Incisor",
        "molar": "Molar",
        "premolar": "Premolar",
        "primaryMolar": "Primary molar",
        "wisdom": "Wisdom tooth",
      },
      "liveTranscriptOffHint":
        "Turn on the live transcript to chart the findings as you dictate",
      "liveTranscriptOffTitle": "Live transcript is off",
      "noChange": "no change",
      "noFindings": "no findings",
      "overflow": "+{{extra}}",
      "overflow_one": "",
      "overflow_other": "",
      "overflowAria_one": "{{count}} more tooth named",
      "overflowAria_other": "{{count}} more teeth named",
      "placedOnPatient": "Placed on patient {{number}}'s chart",
      "repeat": "Please say {{number}} again",
      "repeatShort": "Say it again",
      "state": {
        "charted": "Charted",
        "charting": "Charting",
        "failed": "Not charted",
        "heard": "Heard",
        "removed": "Withdrawn",
        "review": "Chart",
      },
      "toothAria": "Tooth {{number}}",
      "toothLetterAria": {
        "b": "Tooth {{number}}, pontic",
        "bPlanned": "Tooth {{number}}, pontic, planned",
        "f": "Tooth {{number}}, missing",
      },
      "toothStatusAria": "Tooth {{number}}, {{status}}",
      "toothStatusPlannedAria": "Tooth {{number}}, {{status}}, planned",
      "unassigned": "No patient assigned yet",
    },
    "states": {
      "completed": "completed",
      "defective": "defective",
      "existing": "existing",
      "planned": "planned",
      "provisional": "provisional",
    },
    "status": {
      "crowned": "crowned",
      "extracted": "extracted",
      "gap-closed": "gap closed )(",
      "gap-closed-distal": "closed distally )",
      "gap-closed-mesial": "closed mesially (",
      "impacted": "impacted",
      "implant": "implant",
      "missing": "missing",
      "partial-crown": "partial crown",
      "pontic": "pontic",
      "replaced": "replaced",
      "retained-root": "retained root",
      "sound": "sound",
      "telescopic-crown": "telescopic crown",
      "unerupted": "unerupted",
      "veneer": "veneer",
    },
    "statusDefective": {
      "crowned": "Crown needs renewal",
      "partial-crown": "Partial crown needs renewal",
      "pontic": "Pontic needs renewal",
      "replaced": "Replacement needs renewal",
      "telescopic-crown": "Telescopic crown needs renewal",
      "veneer": "Veneer needs renewal",
    },
    "statusPlanned": {
      "crowned": "Crown planned",
      "extracted": "Extraction planned",
      "implant": "Implant planned",
      "partial-crown": "Partial crown planned",
      "pontic": "Pontic planned",
      "replaced": "Replacement planned",
      "telescopic-crown": "Telescopic crown planned",
      "veneer": "Veneer planned",
    },
    "surfaceFinding": "{{surface}} · {{finding}}",
    "surfaces": {
      "B": "buccal/vestibular",
      "C": "cervical",
      "D": "distal",
      "I": "incisal",
      "L": "lingual",
      "M": "mesial",
      "O": "occlusal",
      "P": "palatal",
      "R": "root",
    },
    "toolbar": {
      "confirmDescription":
        "This replaces the whole tooth status: teeth outside the scheme are removed, uncharted scheme teeth are created as sound. Already charted teeth keep their status.",
      "confirmTitle": "Apply dentition preset?",
      "presets": {
        "edentulous": "Edentulous",
        "mixed": "Mixed",
        "primary": "Primary",
      },
    },
    "tooth": {
      "editSurfaceAria": "Edit tooth {{number}}, surface {{surface}}",
      "editToothAria": "Edit tooth {{number}}",
      "examinedAria": "Tooth {{number}}: {{status}}",
      "unexaminedAria": "Tooth {{number}}: unexamined",
    },
  },
  "dzrH1": {
    "link": {
      "ariaLabel": "Look up {{code}} in DZR H1 PRO",
      "lookUp": "Look up in DZR H1 PRO",
      "text": "H1",
      "tooltip": "",
    },
  },
  "embed": {
    "leaveConfirm":
      "A recording is still running. Switching to the other patient will stop it. Switch anyway?",
    "unavailable": {
      "body":
        "The Dentero extension for Teemer is not enabled for your practice yet. Please contact Dentero support.",
      "docsLink": "Read more in the handbook",
      "title": "Not enabled yet",
    },
  },
  "environment": {
    "watermark": {
      "development": "Development",
    },
  },
  "errors": {
    "clipboardCopy": "Could not copy. Please copy manually.",
    "network": "Connection error. Please try again.",
  },
  "featureSpotlights": {
    "actions": {
      "back": "Back",
      "close": "Close tour",
      "done": "Done",
      "handbook": "Open handbook",
      "later": "Later",
      "next": "Next",
      "start": "Show me",
    },
    "befundChartHistory": {
      "card": {
        "body":
          "The patient page shows how the tooth status changed from visit to visit. For every visit that left a tooth status you can open the state afterwards, together with the transcript.",
        "eyebrow": "Set up in your practice",
        "title": "See earlier tooth status",
      },
      "tour": {
        "chart": {
          "body":
            "This is the patient's current tooth status. Click a tooth to see which recording changed it last.",
          "title": "Current tooth status",
        },
        "history": {
          "body":
            "Every visit that left a tooth status has this button. Click it to open the tooth status after that visit and compare it with earlier states.",
          "title": "Open the tooth status history",
        },
        "list": {
          "body":
            "All patients of your practice are listed here. We now open a patient with earlier tooth status versions.",
          "title": "Patient list",
        },
        "sidebar": {
          "body":
            "Patients leads to all treatments of a patient and to their tooth status.",
          "title": "Patients",
        },
      },
    },
    "befundChartLive": {
      "card": {
        "body":
          "Dictate the findings and the tooth status builds up as you speak. Live transcription has to be on for this. With a patient ID the tooth status goes straight into the patient's record, without one it starts on an empty tooth status.",
        "eyebrow": "Set up in your practice",
        "title": "Chart tooth status live",
      },
      "tour": {
        "patient": {
          "body":
            "With the patient ID the tooth status goes straight into this patient's record. Without one it starts on an empty tooth status. If you add the ID later, Dentero assigns it to the patient.",
          "title": "Enter the patient ID",
        },
        "realtime": {
          "body":
            "Live tooth status only runs with live transcription. Switch it on before the recording. With a documentation assistant selected, live tooth status stays off.",
          "title": "Switch on live transcription",
        },
      },
    },
    "befundChartReview": {
      "card": {
        "body":
          "Point at a tooth in a recording and the transcript marks where you dictated it. That is a quick way to check a finding.",
        "eyebrow": "Set up in your practice",
        "title": "Check where a finding came from",
      },
      "tour": {
        "chart": {
          "body":
            "Move over a tooth. The transcript then marks where you dictated it. On older recordings, clicking a tooth shows which recording changed it last.",
          "title": "Check a tooth",
        },
        "open": {
          "body":
            "Click Show chart to unfold the tooth status of this recording.",
          "title": "Open the recording's tooth status",
        },
        "transcript": {
          "body":
            "This opens the transcription. Teeth you named are marked, so you can see whether a finding was dictated that way.",
          "title": "Read it in the transcript",
        },
      },
    },
    "beta": {
      "badge": "Beta",
      "feedback":
        'This feature is still in beta and may have bugs. We welcome your feedback, easiest through "Report" on the documentation.',
    },
    "complexConditions": {
      "card": {
        "body":
          "The magic wand now finds sentences in exact-structure complexes that only apply to some patients and puts them in a condition. Existing complexes do not change on their own. Click the wand once per complex, check the result and save.",
        "eyebrow": "Improved",
        "title": "Detect conditions with AI",
      },
      "tour": {
        "content": {
          "body":
            "Check the result. The editor highlights conditions, placeholders and repetitions in color. If the text stays the same or a notice appears, there is nothing to take over.",
          "title": "Check the result",
        },
        "save": {
          "body":
            "Save. Repeat this for your other complexes with exact structure.",
          "title": "Save",
        },
        "wand": {
          "body":
            'Click the magic wand and choose "Detect placeholders and conditions". The AI puts sentences that only apply to some patients under a condition. This takes a few seconds.',
          "title": "Improve with AI",
        },
      },
    },
    "complexesSetup": {
      "card": {
        "body":
          "Set up recurring treatments once as a complex, with documentation text and billing positions. When someone on the team says the trigger word, Dentero adds the complex to the documentation.",
        "eyebrow": "Included in your plan",
        "title": "Complexes",
      },
      "tour": {
        "autoApply": {
          "body":
            "Switched on, Dentero puts the complex straight into the documentation. Switched off, it stays a suggestion the team applies with one click.",
          "title": "Apply automatically",
        },
        "billing": {
          "body":
            "A complex also carries its billing positions. Click Billing.",
          "title": "Billing positions",
        },
        "content": {
          "body":
            "This text goes into the documentation whenever a trigger word is said. Write it the way it should read in the patient record. The AI can draft or improve it.",
          "title": "Documentation text",
        },
        "create": {
          "body":
            "A complex is a recurring treatment with ready-made text and billing positions. Click Add to create the first one.",
          "title": "Create the first complex",
        },
        "dialog": {
          "body":
            'Trigger words are what you say during the recording so Dentero applies the complex, for example "PZR" or "Ost". Let the AI suggest the content and click Continue.',
          "title": "Set trigger words",
        },
        "positions": {
          "body":
            "Add the GOZ and BEMA positions that belong to this treatment, split by statutory and private insurance (including factor increase and justification). Dentero suggests them together with the text whenever the complex applies.",
          "title": "Add positions",
        },
        "save": {
          "body":
            "Save the complex. From the next recording on, Dentero reacts to the trigger words.",
          "title": "Save",
        },
      },
    },
    "complexesUse": {
      "card": {
        "body":
          "Your practice has set up complexes. Say the trigger word while you record, and Dentero adds the prepared text and billing positions to your documentation.",
        "eyebrow": "Set up in your practice",
        "title": "Complexes",
      },
      "tour": {
        "toggle": {
          "body":
            "Before you record, this shows whether complexes are applied. Leave the switch on and say the trigger word.",
          "title": "Apply complexes",
        },
      },
    },
    "progress": "Step {{current}} of {{total}}",
    "realtimeTranscription": {
      "card": {
        "body":
          "See the text while you treat. When you save, it becomes the basis for documentation and billing, and the audio recording stays as a fallback.",
        "eyebrow": "Included in your plan",
        "title": "Live transcript",
      },
      "tour": {
        "toggle": {
          "body":
            "Switch on the live transcript here before you record. You can turn it on or off for each recording.",
          "title": "Turn on the live transcript",
        },
      },
    },
    "regionLabel": "Discover a feature",
    "tourUnavailable": "The tour can't be shown here right now.",
    "whatsNew": {
      "description":
        "New features in your plan. You can start any tour again here.",
      "empty": "There is nothing new for your practice right now.",
      "failed": "Couldn't load what's new. Please try again later.",
      "loading": "Loading...",
      "startTour": "Start tour",
      "title": "What's new",
    },
  },
  "featureToggle": {
    "confirmationDescription":
      "I hereby order this feature on a paid basis. The amount will be charged on the next invoice.",
    "confirmationTitle": "Enable feature",
    "confirmLabel": "Order now (paid)",
    "dependencyTooltip": "Prerequisite not met",
    "enabledTooltip": "To cancel products, contact support@app.dentero.de",
    "upgradeTooltip": "Please upgrade your subscription to enable this feature",
  },
  "filterableList": {
    "empty": "No entries.",
    "noResults": "No entries found with this filter.",
    "showAll": "Show all entries",
  },
  "format": {
    "billingInterval": {
      "month": "month",
      "year": "year",
    },
    "tenure": {
      "days_one": "{{count}} day",
      "days_other": "{{count}} days",
      "months_one": "{{count}} month",
      "months_other": "{{count}} months",
      "weeks_one": "{{count}} week",
      "weeks_other": "{{count}} weeks",
      "years_one": "{{count}} year",
      "years_other": "{{count}} years",
    },
  },
  "handbook": {
    "learnMore": "Learn more",
  },
  "honorarBenchmark": {
    "apply": "Apply average factor {{factor}}",
    "applyHint": "Click to apply this factor.",
    "badge": "Ø region {{factor}}",
    "compact": "Ø {{factor}}",
    "overview": {
      "averageFactor": "Ø practice",
      "billedCount": "Billed",
      "defaultFactor": "Default factor",
      "description_one":
        "Average factors from the DZR HonorarBenchmark for {{count}} practice within {{radius}} km of postal code {{plz}}, compared with your default factors and your private billing from the last 90 days.",
      "description_other":
        "Average factors from the DZR HonorarBenchmark for {{count}} practices within {{radius}} km of postal code {{plz}}, compared with your default factors and your private billing from the last 90 days.",
      "descriptionEmpty":
        "Average factors from the DZR HonorarBenchmark for practices in your region.",
      "empty":
        "There is no benchmark data for your postal code yet. It is fetched after activation and then every week.",
      "filterLabel": "Filter codes",
      "filters": {
        "all": "All",
        "below": "Below average",
        "billed": "Billed",
        "configured": "With default factor",
      },
      "gap": "Difference",
      "missingPlz":
        "The practice has no postal code. Without one there is no regional comparison.",
      "noMatches": "No codes match this selection.",
      "searchPlaceholder": "Search code or title",
      "setDefault": "Set as default",
      "title": "Fee benchmark",
    },
    "regionColumn": "Ø region",
    "summary": {
      "factor": "Factor {{factor}}",
      "factorWithAmount": "Factor {{factor}} · {{amount}}",
      "title": "Regional average",
    },
    "tooltip_one":
      "DZR HonorarBenchmark: average of {{count}} practice within {{radius}} km of postal code {{plz}}",
    "tooltip_other":
      "DZR HonorarBenchmark: average of {{count}} practices within {{radius}} km of postal code {{plz}}",
  },
  "indexFilters": {
    "all": "All",
    "clearSearch": "Clear search",
    "description": "Use filters to narrow down the list.",
    "entriesFound_one": "{{count}} entry found",
    "entriesFound_other": "{{count}} entries found",
    "filterBy": "Filter by {{label}}",
    "openFilters": "Open filters",
    "optionsLoadError": "Filter options could not be loaded. Please try again.",
    "reset": "Reset filters",
    "resetAll": "Reset all filters",
    "resetNamed": "Reset filter {{label}}",
    "retryOptions": "Try again",
    "searchPlaceholder": "Search...",
    "showingRange": "(Showing {{from}}-{{to}} of {{total}})",
    "title": "Filters",
  },
  "insurance": {
    "short": {
      "private": "PKV",
      "public": "GKV",
    },
  },
  "invitations": {
    "acceptButton": "Accept invitation",
    "invitedLine":
      "You were invited by {{invitedBy}} to join practice {{practiceName}}.",
    "setPassword": "Please set a password for your account.",
  },
  "laboratoryServices": {
    "form": {
      "createAction": "Create",
      "createTitle": "Create laboratory service",
      "description":
        "Create a new laboratory service in the global catalog. You can manage prices afterward.",
      "descriptionPlaceholder": "e.g. cast model, full denture",
      "positionPlaceholder": "e.g. 0010",
      "typePlaceholder": "Select type",
    },
    "index": {
      "add": "Add",
      "description": "Description",
      "kzvPriceReference": "KZV price reference",
      "position": "Position",
      "searchPlaceholder": "Search by position or description",
      "serviceType": "Service type",
      "tabs": {
        "beb": "BEB",
        "bel": "BEL",
      },
    },
    "priceLevels": {
      "high": "High",
      "low": "Low",
      "medium": "Medium",
    },
    "prices": {
      "amountPlaceholder": "e.g. 150.00",
      "bebDescription": "Prices by price level for BEB services.",
      "bebTitle": "BEB prices",
      "belDescription": "Prices by KZV for in-house and external laboratory.",
      "belTitle": "BEL prices",
      "eigenlabor": "In-house laboratory",
      "fremdlabor": "External laboratory",
      "kzv": "KZV",
      "notSet": "Not set",
    },
    "show": {
      "deleteDescription":
        "The laboratory service will be removed from the catalog. Prices already configured in practices remain.",
      "deleteTitle": "Delete laboratory service?",
    },
  },
  "layout": {
    "navigation": {
      "analogPositions": "Analog positions",
      "apiTokens": "API tokens",
      "assistants": "Assistants",
      "auditTrail": "Audit trail",
      "benchmarks": "Benchmarks",
      "billingPositions": "Billing positions",
      "billingReviews": "Billing reviews",
      "chats": "Ask the AI",
      "community": "Dentero Community",
      "complexes": "Complexes",
      "costs": "Costs",
      "customization": "Setup assistant",
      "customizationChats": "Setup chats",
      "dashboard": "Dashboard",
      "defaultFactors": "Default factors",
      "dzrEdition": "DZR Edition",
      "handbook": "Handbook",
      "honorarBenchmark": "Fee benchmark",
      "laboratoryServicePrices": "Lab services",
      "laboratoryServices": "Lab services",
      "materialCatalog": "Materials",
      "patients": "Patients",
      "practice": "Practice",
      "practices": "Practices",
      "prompts": "Prompts",
      "recordDocumentation": "Record documentation",
      "recordDocumentationWithCount_one": "Record documentation ({{count}})",
      "recordDocumentationWithCount_other": "Record documentation ({{count}})",
      "recordings": "Documentation",
      "resumeRecording": "Resume paused recording",
      "resumeRecordingWithCount_one": "Resume paused recording ({{count}})",
      "resumeRecordingWithCount_other": "Resume paused recording ({{count}})",
      "settings": "Settings",
      "subscriptionStripe": "Subscription & Stripe",
      "systemMessages": "System messages",
      "treatments": "Treatments",
    },
    "privacyFilter": {
      "label": "Privacy filter",
    },
    "sections": {
      "administration": "Administration",
      "configuration": "Configuration",
      "integrations": "Integrations",
      "klinik": "Data",
      "management": "Management",
      "resources": "Resources",
    },
    "userMenu": {
      "downloadConnector": "Dentero Connector",
      "installApp": "Install app",
      "language": "Language",
      "logout": "Sign out",
      "manageProfile": "Manage profile",
      "manageSubscription": "Manage subscription",
      "whatsNew": "What's new",
    },
  },
  "markdownEditor": {
    "blockquote": "Quote",
    "bold": "Bold",
    "bulletList": "Bullet list",
    "exitFullscreen": "Exit fullscreen",
    "fullscreen": "Fullscreen",
    "heading1": "Heading 1",
    "heading2": "Heading 2",
    "heading3": "Heading 3",
    "italic": "Italic",
    "orderedList": "Numbered list",
    "redo": "Redo",
    "showEditor": "Show editor",
    "showSource": "Show Markdown source",
    "strikethrough": "Strikethrough",
    "undo": "Undo",
  },
  "materialCatalog": {
    "explainer": {
      "intro":
        "The material catalog shows every material stored in your inventory system {{provider}} — with catalog number, section, name and price.",
      "managerOnly":
        "Only practice owners and practice managers can activate the material catalog.",
      "pointDaily":
        "The catalog is fetched from {{provider}} once a day, and you can refresh it yourself at any time.",
      "pointFuture":
        "When the feature is on, materials found in your documentation are marked.",
      "pointSearch":
        "You can search the entire catalog and filter it by section.",
      "readOnly":
        "The catalog is only read — nothing is changed in {{provider}}.",
    },
    "features": {
      "deactivateConfirmDescription":
        "The material catalog will be hidden and the stored material data will be deleted. Your inventory system stays untouched. You can activate the catalog again at any time.",
      "deactivateConfirmTitle": "Deactivate the material catalog?",
      "toggleLabel": "Material catalog",
    },
    "filters": {
      "category": "Section",
    },
    "list": {
      "columns": {
        "catalogNumber": "Catalog number",
        "category": "Section",
        "description": "Description",
        "name": "Name",
        "price": "Price",
      },
      "empty": "Your material catalog has no entries.",
      "neverSynced": "The catalog has not been fetched yet.",
      "searchPlaceholder": "Search materials …",
      "syncFailed": "The last fetch failed. Please try again.",
      "syncing": "The catalog is being fetched …",
    },
    "sync": {
      "action": "Refresh now",
      "lastError": "Last error: {{message}}",
      "lastSyncedAt": "Last fetched: {{time}}",
      "neverSynced": "Not fetched yet",
      "running": "Fetching …",
      "source": "Source: {{provider}}",
      "states": {
        "failed": "Failed",
        "never": "Not fetched yet",
        "ok": "Up to date",
        "running": "Fetching",
      },
    },
    "title": "Materials",
    "unknownProvider": "your inventory system",
  },
  "microphone": {
    "accessBlocked": "Microphone access blocked",
    "allowAccess": "Please allow microphone access",
    "allowAccessHelp": {
      "chrome":
        "Open the site info icon (lock or tune) in the address bar, set Microphone to Allow, then reload this page.",
      "edge":
        "Open the lock icon in the address bar, set Microphone to Allow, then reload this page.",
      "firefox":
        "Open the permissions icon in the address bar (or the crossed-out microphone), remove the Microphone block, then reload this page. Firefox will ask you again to allow access.",
      "other":
        "Allow microphone access for this site in your browser settings, then reload this page.",
      "safari":
        "Open Safari → Settings → Websites → Microphone, set this site to Allow, then reload this page.",
    },
    "label": "Microphone",
    "lockedHint":
      "Finish or discard the current recording to switch microphones.",
    "noAudioFromDevice":
      "On Windows another program — practice software, dictation or softphone tools — can claim the microphone exclusively. Close those programs or select a different microphone.",
    "noSignal": "No microphone signal detected",
    "select": "Select microphone",
    "signalHint":
      "If your microphone is working, you should see green bars here.",
    "testStartFailed": "Could not start the microphone.",
    "unnamedDevice": "Microphone {{id}}...",
    "unsupported":
      "Audio recording is not available in this browser. Please use a current browser such as Chrome, Firefox, or Safari.",
    "unsupportedShort": "Audio recording is not available in this browser",
  },
  "newVersionToast": {
    "appUpdateAction": "Update now",
    "appUpdateTitle": "A new app version is available",
    "later": "Later",
    "recordingInProgress": "Finish the recording first, then you can reload.",
    "recordingInProgressAppUpdate":
      "Finish the recording first, then you can update.",
    "reload": "Reload",
    "title": "A new version is available",
  },
  "patients": {
    "briefing": {
      "disclaimer":
        "AI-generated. Please double-check. Not a medical recommendation.",
      "empty": "The briefing is created after the next documentation.",
      "pending": "The briefing is being updated.",
      "title": "Briefing",
      "updatedAt": "As of {{date}}",
    },
    "chart": {
      "currentStateWithDate": "Current state · {{date}}",
      "empty": "No findings chart for this patient yet.",
      "historyTitle": "Tooth status history",
      "position": "{{current}} of {{total}}",
      "proposal": {
        "confirm": "Apply",
        "description":
          "Proposed from the documentation. The chart shows a preview until you apply it.",
        "failed": "The proposal could not be applied.",
        "queued_one": "{{count}} more proposal is still being prepared.",
        "queued_other": "{{count}} more proposals are still being prepared.",
        "title_one": "{{count}} proposed change",
        "title_other": "{{count}} proposed changes",
      },
      "stepNewer": "Newer state",
      "stepOlder": "Older state",
      "title": "Tooth status",
      "unexamined": "No teeth examined yet.",
      "viewingHistorical": "Historical state from {{date}} — view only.",
      "viewState": "Show the tooth status left by this visit",
      "viewStateShort": "Tooth status",
    },
    "index": {
      "lastVisit": "last {{date}}",
      "searchPlaceholder": "Search by patient number",
      "sort": {
        "countAsc": "Number of documentations (ascending)",
        "countDesc": "Number of documentations (descending)",
        "recent": "Most recent documentations",
      },
      "timespan": {
        "clear": "Clear time period filter",
        "label": "Time period",
        "options": {
          "custom": "Custom",
          "last_30_days": "Last 30 days",
          "last_7_days": "Last 7 days",
          "last_90_days": "Last 90 days",
          "last_month": "Last month",
          "this_month": "This month",
        },
      },
      "treatmentCount_one": "{{count}} documentation",
      "treatmentCount_other": "{{count}} documentations",
    },
    "label": "Patient {{number}}",
    "timeline": {
      "empty": "There are no documentations for this patient yet.",
      "treatmentCount_one": "{{count}} documentation",
      "treatmentCount_other": "{{count}} documentations",
    },
  },
  "planLocked": {
    "billingTitle": "Billing",
    "complexesTitle": "Complexes",
    "features": {
      "analog_positions": {
        "description":
          "Store the analog positions under § 6 (1) GOZ your practice uses, each with a fixed amount. They are suggested in private (PKV) billing as soon as the documentation describes a service billed by analogy.",
        "title": "Analog positions",
      },
      "complexes": {
        "description":
          "Complexes are prepared treatment sequences for your practice. Say the keyword during the recording and the stored sequence and its billing positions are added to the documentation.",
        "title": "Complexes",
      },
      "documentation_assistant": {
        "description":
          "The documentation assistant shows a checklist for the current treatment type while you record, so nothing that matters for complete documentation and billing is left out. Assistants can be tailored to your practice.",
        "title": "Assistants",
      },
      "factors": {
        "description":
          "Define the factor and justification with which GOZ and GOÄ positions are suggested. Factor and justification are carried into every billing suggestion.",
        "title": "Default factors",
      },
      "laboratory_service_prices": {
        "description":
          "Maintain your in-house lab prices by BEB and BEL. Lab services in the documentation are recognized and suggested with your practice prices for billing.",
        "title": "Laboratory services",
      },
      "material_catalog": {
        "description":
          "Materials found in the documentation are marked, and the materials from your inventory system are listed. Practice managers turn the feature on or off.",
        "title": "Material catalog",
      },
    },
    "includedIn": "Included in the {{tier}} package.",
    "page": {
      "handbookLink": "Read in the handbook",
      "noPortalHint": "To change your package, contact support@app.dentero.de.",
      "tierNote":
        "This feature is included in the {{tier}} package. Your current package does not include it.",
      "upgradeHint":
        "Change your package in the Stripe customer portal. New modules are available right after the switch.",
      "upgradeLink": "Change package in Stripe",
    },
    "plausibilityTitle": "Plausibility",
    "tiers": {
      "exclusive": "Exclusive",
      "premium": "Premium",
    },
    "tooltip": "Not included in your current package.",
  },
  "practiceAnalogPositions": {
    "bzaekSource": "BZÄK source",
    "deleteDescription":
      "The position will no longer be used for manual selection or suggestions in this practice.",
    "deleteTitle": "Remove analog position from practice?",
    "description":
      "Select the analog positions used in this practice and set the fixed amount. The BZÄK hourly rate of 407 EUR can serve as a reference.",
    "empty": "No analog positions configured for this practice yet.",
    "form": {
      "amountLabel": "Amount in EUR",
      "amountPlaceholder": "e.g. 407.00",
      "calculatedAmountLabel": "Calculated amount:",
      "createOption": 'Create new analog position "{{label}}"',
      "desiredReferenceFactorLabel": "Factor",
      "desiredReferenceFactorPlaceholder": "e.g. 2.3",
      "highFactorHint":
        "A factor higher than 2.3 should only be used in rare exceptional cases, as it may cause reimbursement issues.",
      "modeAmount": "Target amount",
      "modeReference": "GOZ/GOÄ position",
      "namingHint":
        "The name of the analog position is used to detect from the documented treatment text when this analog position applies. Choose the name carefully so it is clear and understandable.",
      "positionLabel": "Analog position",
      "positionPlaceholder": "Select or create analog position",
      "referenceFactorLabel": "Factor",
      "referencePositionPlaceholder": "Select position",
    },
    "import": "Import",
    "referenceSuggestions": {
      "empty": "No matching GOZ/GOÄ references for this amount.",
      "heading": "Reference suggestions",
      "loading": "Loading suggestions …",
      "summary":
        "Factor {{factor}} · Reference amount {{billed}} · Deviation from target amount {{difference}}",
    },
    "table": {
      "amount": "Euro",
      "position": "Position",
      "referenceCode": "GOZ/GOÄ",
      "referenceFactor": "Factor",
    },
    "title": "Analog positions",
  },
  "practiceCosts": {
    "billingExempt": "Billing-exempt",
    "details": {
      "cachedTokens": " / Cached {{total}}",
      "columns": {
        "amount": "Amount",
        "cost": "Cost",
        "interval": "Interval",
        "model": "Model",
        "position": "Position",
        "quantity": "Quantity",
        "requests": "Requests",
        "tokens": "Tokens",
        "type": "Type",
      },
      "context": {
        "activeComplexes": "Active complexes",
        "activeMembers": "Active members",
        "activeMembersValue": "{{active}} of {{total}}",
        "audioRetention": "Audio retention",
        "billingFeature": "Billing feature",
        "customPrompts": "Custom prompts",
        "description":
          "Additional signals that affect costs or cost understanding",
        "factors": "Factor increase",
        "title": "Other relevant context data",
      },
      "noSubscription": "No associated subscription.",
      "requestsByType": {
        "description":
          "Aggregated by request type and model with token usage and estimated costs",
        "empty": "No AI requests in the analysis period.",
        "title": "AI requests by type",
      },
      "subscriptionContext": {
        "noDirectMargin": "No direct margin allocation",
        "shared": "Shared",
        "title": "Subscription context",
      },
      "tokenBreakdown": "In {{input}} / Out {{output}}",
      "unpricedRequests": "{{total}} without cost basis",
    },
    "sharedSubscription_one": "Shared subscription across {{count}} practice",
    "sharedSubscription_other":
      "Shared subscription across {{count}} practices",
    "stats": {
      "activeUsers": {
        "description": "With recordings in period",
        "footer_one": "{{count}} user currently in practice",
        "footer_other": "{{count}} users currently in practice",
        "title": "Active users",
      },
      "aiCosts": {
        "description": "Estimated internal costs",
        "title": "AI costs",
      },
      "aiRequests": {
        "description": "All request types",
        "title": "AI requests",
      },
      "audioTime": {
        "description": "Recording volume",
        "footer_one": "{{count}} recording",
        "footer_other": "{{count}} recordings",
        "title": "Audio time",
      },
      "costPerDoc": {
        "description": "Average",
        "footer": "{{value}} / audio min.",
        "title": "Cost / doc",
      },
      "documentations": {
        "description": "Newly created",
        "title": "Documentation",
      },
      "failed": {
        "description": "AI requests",
        "title": "Failed",
      },
      "margin": {
        "title": "Margin",
      },
      "retried": {
        "description": "Requests with multiple attempts",
        "title": "Retried",
      },
      "seats": {
        "description_one": "{{count}} active member",
        "description_other": "{{count}} active members",
        "footer": "Currently paid seats",
        "title": "Seats",
      },
      "subscriptionValue": {
        "footer": "Paid days only {{windowLabel}}",
        "noActive": "No active subscription",
        "title": "Subscription value",
      },
    },
  },
  "practiceDefaultFactors": {
    "deleteDescription":
      "The default factor will no longer apply to new suggestions or manually added positions. Existing treatments stay unchanged.",
    "deleteTitle": "Remove default factor?",
    "description":
      "Set the factor and justification used whenever a GOZ or GOÄ position is suggested.",
    "empty": "No default factors are set up for this practice yet.",
    "form": {
      "create": "Add default factor",
      "description":
        "The factor and justification are applied to every new suggestion of this position.",
      "editTitle": "Edit default factor",
      "factorLabel": "Factor",
      "factorPlaceholder": "e.g. 3.5",
      "justificationCatalogEmpty": "No predefined justifications",
      "justificationCatalogLabel": "Predefined justification",
      "justificationCatalogPlaceholder": "Select a justification",
      "justificationLabel": "Justification",
      "justificationPlaceholder": "Free text stored on the billing position",
      "newTitle": "Set up default factor",
      "positionLabel": "Position",
      "positionPlaceholder": "Choose a GOZ or GOÄ position",
    },
    "table": {
      "code": "Position",
      "factor": "Factor",
      "justification": "Justification",
      "title": "Title",
      "type": "Type",
    },
    "title": "Default factors",
  },
  "practiceLaboratoryServicePrices": {
    "index": {
      "overridden": "Overridden",
    },
    "prices": {
      "bebDescription": "Practice-specific BEB prices by price level.",
      "catalogDefault": "Catalog default",
      "reset": "Reset",
      "resetDescription":
        "The practice price will be removed and the catalog price will be used again.",
      "resetTitle": "Reset price?",
    },
  },
  "practiceMemberships": {
    "activateUser": "Activate user",
    "currentUser": "This is you",
    "deactivateUser": "Deactivate user",
    "demoteDialog": {
      "description":
        "The user will lose admin status and access to admin functions.",
      "title": "Remove admin rights?",
    },
    "documentationUsed_one": "{{count}} documentation ({{month}})",
    "documentationUsed_other": "{{count}} documentations ({{month}})",
    "documentationUsedThisPeriod_one":
      "{{count}} in the current billing period",
    "documentationUsedThisPeriod_other":
      "{{count}} in the current billing period",
    "emailLockedHint":
      "Only the user can change their own login email, in their profile.",
    "existingUserLookup": {
      "accountName": "Account: {{name}}.",
      "alreadyMember": "This user is already a member of the practice.",
      "continue": "Continue",
      "denteroManagedExisting":
        "This email address cannot be added to this practice because it is already assigned to another practice. Please choose a different email address.",
      "existingAccount":
        "This email address already belongs to an existing Dentero account.",
      "existingAccountInPractices_one":
        "This email address already belongs to an existing Dentero account (in one other practice).",
      "existingAccountInPractices_other":
        "This email address already belongs to an existing Dentero account (in {{count}} other practices).",
      "existingAccountWillAdd":
        "The existing account will be added to this practice.",
      "failed": "The email address could not be checked.",
      "noPermission":
        "You cannot add this account yourself because it also belongs to practices you do not manage, or it is a Dentero staff account. Contact Dentero support.",
      "rateLimited": "Too many checks. Wait a moment and try again.",
      "staysDeactivated":
        "The account is deactivated and stays deactivated after it is added here.",
    },
    "formDialog": {
      "editTitle": "Edit user",
      "newTitle": "Create new user",
      "trialingDescription":
        "Seats are free during the trial. After the trial you pay for every booked seat. You can remove free seats before then.",
    },
    "inactive": "Inactive",
    "lastActive": "Last active:",
    "manageUploadLink": "Manage upload link",
    "never": "never",
    "promoteToAdmin": "Promote to admin",
    "removeAdminRights": "Remove admin rights",
    "removeFromPractice": "Remove from practice",
    "reviewer": {
      "description": "Allows reviewing AI billing suggestions on recordings.",
      "title": "Reviewer for billing suggestions",
    },
    "reviewerPermission": "Additional permission for billing review",
    "rolePlaceholder": "Select role",
    "roles": {
      "manager": "Manager",
      "user": "User",
    },
    "seatCharge": {
      "activateConfirm": "Activate and buy a paid seat",
      "activateTitle": "Activate user?",
      "createConfirm": "Create and buy a paid seat",
      "inviteConfirm": "Invite and buy a paid seat",
      "note":
        "All seats are taken. This buys 1 more seat for {{price}} per {{interval}} plus VAT. The prorated amount for the current billing period appears on the next invoice.",
      "noteNoPrice": "All seats are taken. This buys 1 more paid seat.",
      "noteTrial":
        "All seats are taken. This buys 1 more seat. It costs {{price}} per {{interval}} plus VAT after the trial.",
    },
    "sendInvite": "Send invitation",
    "uploadLink": {
      "description":
        "Enable the link for this user so audio files can be uploaded without signing in.",
      "publicLinkLabel": "Public upload link",
    },
    "uploadLinkActive": "Upload link active",
  },
  "practices": {
    "apiTokens": {
      "createdHint": "Copy this token now — you won't be able to see it again.",
      "createdTitle": "Token created",
      "description":
        "API tokens grant read access to this practice's recordings and documentation through the Dentero API. Each token acts as the selected practice member.",
      "empty": "No API tokens yet.",
      "generate": "Generate token",
      "generateDescription":
        "Create a token to access this practice's data through the Dentero API as a specific practice member. It is shown only once.",
      "lastUsed": "last used {{date}}",
      "nameLabel": "Name",
      "namePlaceholder": "e.g. PMS-integration",
      "neverUsed": "never used",
      "revoke": "Revoke",
      "revokeConfirmDescription":
        "Applications using this token will immediately lose access. This cannot be undone.",
      "revokeConfirmTitle": "Revoke this token?",
      "title": "API tokens",
      "tokenMeta": "•••• {{lastFour}} · created {{created}}",
      "userLabel": "User",
      "userMeta": "for {{name}}",
      "userPlaceholder": "Select a practice member",
    },
    "assistants": {
      "edit": {
        "contentLabel": "Content",
        "contentPlaceholder": "Content for this assistant...",
        "titleLabel": "Title",
      },
      "form": {
        "continue": "Continue",
        "description":
          "Create your own documentation assistant for this practice.",
        "newTitle": "New assistant",
        "titleLabel": "Title",
        "titlePlaceholder": "e.g. Emergency documentation",
      },
      "index": {
        "add": "Add",
        "breadcrumb": "Documentation assistants",
        "deleteDescription":
          "Are you sure you want to delete this assistant? This action cannot be undone. For an override, the global assistant will fall back afterwards.",
        "deleteTitle": "Delete assistant",
        "empty": "No assistants yet.",
        "inherited": "Global",
        "override": "Override",
      },
    },
    "costs": {
      "billingInterval": {
        "monthly": "Monthly",
        "yearly": "Yearly",
      },
      "fixedWindow": "Since {{date}}",
      "marginDescription": {
        "direct": "Subscription minus AI costs",
        "noSubscription": "0 EUR revenue minus AI costs",
        "partial": "Partial cost basis",
        "unattributable": "Not directly attributable",
      },
      "marginFooter": {
        "default": "Subscription value in analysis period minus AI costs",
        "noSubscription": "No subscription revenue in analysis period",
        "shared": "Subscription is shared with other practices",
      },
      "noSubscription": "No subscription",
      "retention": {
        "1_week": "1 week",
        "24_hours": "24 hours",
        "immediately": "Immediately",
        "indefinite": "Indefinite",
      },
      "subscriptionPeriodDescription":
        "Current Stripe-like configuration, prorated to paid days {{windowLabel}}",
      "subscriptionStatus": {
        "active": "Active",
        "canceled": "Canceled",
        "incomplete": "Incomplete",
        "incomplete_expired": "Expired",
        "past_due": "Payment due",
        "paused": "Paused",
        "trialing": "Trial",
        "unpaid": "Unpaid",
      },
      "unknown": "Unknown",
    },
    "deletion": {
      "confirmSelectedSchedule": "Selected deletion time: {{schedule}}",
      "deleteImmediately": "Delete immediately",
      "description":
        "Are you sure this practice should be marked for deletion?",
      "immediateWarning": "Immediate deletion cannot be undone!",
      "mark": "Mark for deletion",
      "options": {
        "immediately": "Immediately",
        "oneWeek": "in one week",
        "thirtyDays": "in 30 days",
      },
      "removeMarker": "Remove deletion marker",
      "title": "Really mark practice for deletion?",
    },
    "details": {
      "acceptRisk": "Accept",
      "additionalAddress": "Address line 2",
      "address": "Address",
      "audioRetention": "Audio recording retention period",
      "audioRetentionTooltip":
        "Audio recordings are automatically deleted after the selected period.<br />Pseudonymized documentation is retained.",
      "changeRetentionDescription":
        "All audio recordings older than {{period}} will be permanently deleted when this setting is applied.",
      "changeRetentionTitle": "Change retention period?",
      "city": "City",
      "confirmRetention": "Confirm",
      "correctionNeedsRawAsr":
        "Only available when transcription runs on Soniox, Mistral or AssemblyAI.",
      "deletion": "Delete practice",
      "deletionNone": "No scheduled deletion",
      "deletionScheduledStatus": "Deletion scheduled ({{date}})",
      "deletionTooltip":
        "After the selected time, the practice will be completely and permanently deleted.<br />You can cancel an active deletion marker as long as the practice has not been deleted yet.",
      "enableImmediateDeletionTitle": "Enable immediate deletion?",
      "immediateDeletionDescription":
        "When enabled, audio recordings are permanently deleted immediately after successful transcription. Transcription accuracy can no longer be verified against the original recording.<br /><br /><bold>Warning:</bold> All existing audio recordings for this practice will also be permanently deleted.<br /><br />By enabling this, you accept this risk.",
      "kzv": "KZV",
      "name": "Name",
      "notProvided": "Not provided",
      "pvsSystem": "Practice management system",
      "transcriptCorrectionModel": "Transcript correction model",
      "transcriptionModel": "Transcription model",
      "useDefaultSetting": "Use default",
      "useGlobalSetting": "Use global setting",
      "usingDefaultSetting": "Currently using the default {{label}}.",
      "usingGlobalSetting": "Currently using global setting {{label}}.",
      "zip": "ZIP",
    },
    "features": {
      "analogPositions": "Analog positions (PKV)",
      "automaticBilling": "Automatic billing",
      "befundChart": "01 findings chart",
      "beta": "Beta",
      "billing": "Billing",
      "chunkedDocumentationCopy": "Chunked copying",
      "chunkedDocumentationCopyInfo": "Information about chunked copying",
      "chunkedDocumentationCopyTooltip":
        "Enables the option to copy documentation in chunks. This is relevant for PVS systems that do not allow documentation longer than 1000 characters.",
      "complexes": "Complexes",
      "confirmation": {
        "enableAnalogPositionsDescription":
          "Do you want to enable PKV analog services and analog position setup for this practice?",
        "enableAnalogPositionsTitle": "Enable analog positions",
        "enableComplexesDescription":
          "Do you want to enable complexes for this practice?",
        "enableComplexesTitle": "Enable complexes",
        "enableDokuAssistantDescription":
          "Do you want to enable the documentation assistant for this practice?",
        "enableDokuAssistantTitle": "Enable documentation assistant",
        "enableFeatureDescription":
          'Do you want to enable feature "{{label}}" for this practice?',
        "enableFeatureTitle": "Enable {{label}}",
      },
      "connector": "Dentero Connector",
      "customizationChat": "Setup assistant",
      "dependencyBilling": "Billing must be enabled",
      "dependencyTeemer": "Teemer must be connected",
      "dokuAssistant": "Documentation assistant",
      "factors": "Factors & justifications",
      "integrationsTitle": "Integrations & customizations",
      "laboratoryServicePrices": "Laboratory services",
      "orderDescription":
        'I hereby order feature "{{featureLabel}}" for {{licenseCount}} {{userWord}} ({{perUser}}/{{userWord}}/{{intervalLabel}}, total {{total}}/{{intervalLabel}}). The amount will be charged on the next invoice.',
      "orderWithoutPrice":
        'I hereby order feature "{{featureLabel}}" on a paid basis.',
      "patientBriefing": "Patient briefing",
      "plausibilityCheck": "Plausibility check",
      "plausibilityCoverage": "Plausibility across visits",
      "realtimeTranscription": "Live transcription",
      "teemerBillingCheck": "Billing check",
      "teemerExtension": "Teemer extension for Chrome",
      "theme": "Theme",
      "title": "Features",
      "trialDateSuffix": " (ends {{date}})",
      "trialDescription":
        '"{{featureLabel}}" is available free as a preview during your trial period{{datePart}}. When enabled, the feature becomes paid only after the trial ends: {{perUser}}/{{userWord}}/{{intervalLabel}} ({{total}}/{{intervalLabel}} for {{licenseCount}} {{userWord}}).',
      "upgradeAutomaticBilling": "Only admins can enable automatic billing",
      "upgradeBeta": "Only admins can enable beta features",
      "userWord_one": "user",
      "userWord_other": "users",
    },
    "form": {
      "createTitle": "Create new practice",
      "emailHint":
        "This email is used to forward emails when treaters do not have their own email addresses.",
    },
    "index": {
      "add": "Add",
      "cachedAt": "Data as of:",
      "churnRisk": "Churn risk",
      "churnRiskTooltip":
        "Churn risk: No login and no documentation in the last 30 days. This practice may cancel.",
      "churnValues": {
        "risk": "Churn risk",
      },
      "credits": "Credits",
      "creditsPracticeUsed": "{{used}} from this practice",
      "creditsRemaining": "{{remaining}} left",
      "creditsValues": {
        "reached": "Quota reached",
      },
      "deletionScheduled": "Deletion: {{date}}",
      "empty": "No practices yet.",
      "lastRecording": "Last recording",
      "members": "Members",
      "priceValues": {
        "legacy": "Legacy",
        "older": "Older price",
      },
      "recordings": "Recordings",
      "searchPlaceholder": "Search by practice, city, address, or member",
      "sinceUntil": "From/To",
      "stats": {
        "cost": {
          "short": "Cost",
        },
        "margin": {
          "title": "Margin",
        },
        "revenue": {
          "short": "Rev.",
        },
      },
      "status": "Status",
      "statusValues": {
        "cancelled": "Canceled",
        "markedForDeletion": "Marked for deletion",
        "noStatus": "No status",
        "subscribed": "Subscribed",
        "testing": "Trial",
      },
      "tierValues": {
        "basic": "Basic",
        "exclusive": "Exclusive",
        "premium": "Premium",
      },
      "windowStartsAt": "Calculated since:",
    },
    "integrations": {
      "ivoris": {
        "connectionError":
          "Connection to Ivoris was not successful. Please check your entries.",
        "description": "Please enter the required data for Ivoris.",
        "linknameLabel": "Link name",
        "loadingProfiles": "Loading...",
        "loadProfiles": "Load profiles",
        "profileLabel": "Profile",
        "profilePlaceholder": "Load profiles to make a selection",
        "selectProfileError": "Please select a profile.",
        "title": "Ivoris integration",
      },
      "teemer": {
        "appointmentStatistics": {
          "available": "Available",
          "check": "Check now",
          "checkedAt": "Last checked: {{date}}",
          "neverChecked": "Not checked yet",
          "title": "Appointment data",
          "unavailable": "Unavailable",
          "unknown": "Unknown",
        },
        "autoSync":
          "When the documentation is ready, it is sent to the matching Teemer session without a review step.",
        "autoSyncBilling":
          "After the documentation, the selected billing positions for the insurance Teemer reports for the session follow. Sent positions can only be removed in Teemer.",
        "autoSyncBillingTitle": "Send billing to Teemer automatically",
        "autoSyncTitle": "Send documentation to Teemer automatically",
        "chunking":
          "Teemer accepts a maximum of 1000 characters per comment. When this option is active, longer documentation is automatically split into multiple parts so it remains editable in Teemer. When inactive, longer documentation is rejected by Teemer.",
        "chunkingTitle": "Transfer documentation in multiple parts",
        "description": "Please enter the required data for Teemer.",
        "domainExample":
          "For example <example>pvsXX.teemer.de/praxissteuerung</example>",
        "domainLabel": "Domain",
        "sealHelp":
          "Single: <single>TT</single> or multiple separated by commas: <multiple>TT, AB, XY</multiple>",
        "sealShortName": "Stamp abbreviation",
        "title": "Teemer integration",
        "tokenLabel": "Token",
      },
    },
    "ivoris": {
      "noRedisError": "No Redis error detected",
      "noStoredError": "No error stored",
      "notConfigured": "Not configured",
      "patientCacheDescription": "Redis status of Ivoris patient mapping.",
      "patientCacheTitle": "Patient cache",
      "rows": {
        "cachePresent": "Cache present",
        "integration": "Integration",
        "lastError": "Last error",
        "lastJobRun": "Last job run",
        "lastSuccessfulSync": "Last successful sync",
        "lastSynchronisedTreatment": "Last synchronized treatment",
        "patientsInCache": "Patients in cache",
        "profileId": "Profile ID",
        "profileIndex": "Profile index",
        "redisError": "Redis error",
        "redisKey": "Redis key",
        "status": "Status",
        "synchronisedTreatments": "Synchronized treatments",
      },
      "startSync": "Start sync",
      "status": {
        "failure": "Errors present",
        "ok": "OK",
        "running": "Running",
      },
      "statusDescription":
        "Integration, synchronization, and documentation status.",
      "statusTitle": "Status",
      "syncRunning": "Sync running",
      "timestampMissing": "Not currently stored",
      "unreadable": "Unreadable",
    },
    "seats": {
      "addAction": "Add seats",
      "addConfirm_one": "Buy {{count}} seat",
      "addConfirm_other": "Buy {{count}} seats",
      "addDescription": "How many seats do you want to add?",
      "addPendingNotice": "A removal is already pending. Undoing it is free.",
      "addProrated":
        "The prorated amount for the rest of the billing period appears on the next invoice.",
      "addResult": "Then {{total}} seats",
      "addResultPrice":
        "Then {{total}} seats, {{amount}} per {{interval}} plus VAT",
      "addTitle": "Add seats",
      "barLabel": "{{occupied}} seats occupied, {{free}} empty",
      "cancelRemoval": "Cancel removal",
      "countLabel": "Number of seats",
      "decrease": "Decrease count",
      "hintAllPending": "All empty seats are already marked for removal.",
      "hintOccupied": "Deactivate a user first to free a seat.",
      "increase": "Increase count",
      "legend":
        "<occupied>{{occupied}}</occupied> occupied · <free>{{free}}</free> empty",
      "legendPending_one": "{{count}} will be removed on {{date}}",
      "legendPending_other": "{{count}} will be removed on {{date}}",
      "pendingNotice_one":
        "From {{date}}, {{next}} instead of {{current}} seats. Until then the seat stays paid and usable.",
      "pendingNotice_other":
        "From {{date}}, {{next}} instead of {{current}} seats. Until then the seats stay paid and usable.",
      "pricePerSeat": "{{price}} per seat and {{interval}} plus VAT",
      "removeAction": "Remove empty seats",
      "removeConfirm_one": "Remove {{count}} seat on {{date}}",
      "removeConfirm_other": "Remove {{count}} seats on {{date}}",
      "removeConfirmNoDate_one": "Remove {{count}} seat",
      "removeConfirmNoDate_other": "Remove {{count}} seats",
      "removeDescription": "How many empty seats do you want to remove?",
      "removeSummary_one":
        "Until {{date}}, {{current}} seats stay paid and usable. From {{nextDate}}, {{next}} seat. No credit.",
      "removeSummary_other":
        "Until {{date}}, {{current}} seats stay paid and usable. From {{nextDate}}, {{next}} seats. No credit.",
      "removeSummaryNoDate_one":
        "At the end of the billing period, {{next}} seat remains. No credit.",
      "removeSummaryNoDate_other":
        "At the end of the billing period, {{next}} seats remain. No credit.",
      "removeTitle": "Remove empty seats",
      "sharedWith_one":
        "Shared subscription with {{count}} other practice. The seats count for every practice on it.",
      "sharedWith_other":
        "Shared subscription with {{count}} other practices. The seats count for every practice on it.",
      "summary_one": "{{occupied}} of {{count}} seat occupied",
      "summary_other": "{{occupied}} of {{count}} seats occupied",
      "title": "Seats",
    },
    "settings": {
      "complexesAppliedByDefault": "Apply complexes by default",
      "documentationAssistantEnabledByDefault":
        "Enable documentation assistant by default",
      "documentationDownloadFormat": "Download documentation as",
      "documentationDownloadFormatInfo":
        "Information about the format of downloaded documentation",
      "documentationDownloadFormatOptions": {
        "pdf": "PDF (.pdf)",
        "txt": "Text file (.txt)",
      },
      "documentationDownloadFormatTooltip":
        "Applies to every documentation download button on the recording page and in the recordings list. The PDF includes the practice name, treatment date and patient number.",
      "protectPrivacy": "Protect privacy",
      "protectPrivacyInfo": "Information about protecting privacy",
      "protectPrivacyTooltip":
        "When enabled, only the creator of a recording can play or download the audio recording and view the raw transcription.",
      "realtimeTranscriptionEnabledByDefault":
        "Enable live transcription by default",
      "realtimeTranscriptionEnabledByDefaultInfo":
        "Information about live transcription",
      "realtimeTranscriptionEnabledByDefaultTooltip":
        "Controls whether the live transcript is already on for new recordings. It transcribes directly in the browser while recording; otherwise the recording is transcribed after upload. Practices can always switch it on per recording.",
      "skipAiRequests": "Skip AI requests (testing)",
      "skipAiRequestsInfo": "About skipping AI requests",
      "skipAiRequestsTooltip":
        'For non-production testing only. When on, new recordings are not transcribed and no AI documentation or billing runs — they appear as "No speech detected". Saves AI provider cost.',
      "title": "Settings",
    },
    "show": {
      "addMember": "Add",
      "emptyMembers": "No treaters yet.",
      "ivoris": "Ivoris",
      "membersHeading": "Users",
      "scheduledDeletion": "Scheduled deletion on {{date}}",
    },
    "subscription": {
      "badges": {
        "legacyTransition": "Legacy transition",
        "manual": "Manual",
        "noStripe": "No Stripe subscription",
        "packageModel": "Package model",
      },
      "billingModel": {
        "legacyTransition": "Legacy transition",
        "manual": "Manual",
        "none": "No subscription",
        "preStripe": "Pre-Stripe",
        "tier": "Tier",
      },
      "fields": {
        "interval": "Interval",
        "package": "Package",
        "seats": "Seats",
        "status": "Status",
      },
      "internal": {
        "adminBadge": "Admin",
        "description":
          "For migration, discounts, and support cases. Normal customer changes go through the Stripe customer portal.",
        "title": "Internal Stripe management",
      },
      "modules": {
        "activeCount": "{{active}}/{{total}} active",
        "heading": "Modules in package",
        "headingTrialing": "Modules during trial",
        "noneIncluded": "This package includes no additional modules.",
        "trialingHint":
          "All modules are active during the trial. After the trial ends, only modules included in the paid package remain active.",
      },
      "overview": {
        "title": "Subscription overview",
      },
      "package": {
        "legacyFeatureSubscription": "Previous feature subscription",
        "manualAgreement": "Manual agreement",
        "none": "No subscription",
      },
      "portal": {
        "description":
          "Manage package, invoices, and payment details in the Stripe customer portal.",
        "manageButton": "Manage subscription in Stripe",
        "noStripeBadge": "No Stripe subscription",
        "title": "Subscription & billing",
      },
      "priceMove": {
        "atRenewal":
          "The new price applies from the next renewal. The current month stays at the old price.",
        "confirm": "Apply price",
        "description":
          "Shows the next invoice before the current catalog price is applied.",
        "descriptionLegacy":
          "Moves the previous feature subscription onto the chosen package. Add-ons are removed without a credit. The preview shows the next invoice first.",
        "effectiveAt": "Effective on",
        "preview": "Preview",
        "sharedRefusal":
          "Shared subscriptions are not moved here. Change them manually in Stripe.",
        "targetTier": "Package",
        "title": "Move to the current catalog price",
        "total": "Next invoice",
      },
      "priceStatus": {
        "legacy": "Legacy",
        "noAddons": "No add-ons",
        "older": "Older price",
        "perSeat": "{{amount}} per seat",
        "unavailable": "Stripe did not return a price.",
      },
      "provision": {
        "description":
          "Sends the practice a Dentero link. Stripe Checkout starts only when the practice opens it, and links the subscription after successful payment.",
        "fieldInterval": "Interval",
        "fieldPackage": "Package",
        "seatsHint":
          "Seats for checkout are derived from active users in the practice. Minimum 1 seat. The link is valid for 7 days.",
        "submit": "Send link",
        "title": "Send checkout link",
        "trialDescription":
          "Off = subscription starts immediately. On = 14-day trial, same as signup.",
        "trialLabel": "Free trial",
      },
      "seatsOccupied": "{{used}}/{{limit}} in use",
      "subscriptionStatus": {
        "active": "Active",
        "canceled": "Canceled",
        "incomplete": "Payment pending",
        "none": "No subscription",
        "pastDue": "Payment overdue",
        "paused": "Paused",
        "trialing": "Trial",
        "unpaid": "Unpaid",
      },
    },
    "usage": {
      "exhausted": "The documentation pool is used up",
      "period": "{{start}} to {{end}}",
      "perSeat_one": "{{limit}} per seat × {{count}} seat",
      "perSeat_other": "{{limit}} per seat × {{count}} seats",
      "sharedBy": "Every practice on the subscription shares this pool:",
      "title": "Documentation usage",
      "usedOfLimit": "{{used}} / {{limit}}",
    },
  },
  "pwaInstall": {
    "description": "Install as an app for quick access.",
    "dismissAriaLabel": "Close",
    "safari": {
      "step1": "Click File in the menu bar.",
      "step2": 'Choose "Add to Dock...".',
    },
    "stepsLabel": "How to:",
    "title": "Install {{brand}}",
    "treatmentDescription":
      "Install as an app for quick access during treatment, without browser tabs.",
  },
  "recordingDrafts": {
    "chunkPersistenceInterrupted":
      "Local caching was interrupted. The recording captured so far is preserved.",
    "localReadFailed":
      "The local recording could not be read. Please retry the upload.",
    "missingLocalRecording": "The local recording could no longer be found.",
    "pruned_one":
      "The oldest local recording in the upload queue was removed to free up storage.",
    "pruned_other":
      "The {{count}} oldest local recordings in the upload queue were removed to free up storage.",
    "recovered":
      "The recording was restored locally and is waiting for your next step.",
    "savedOffline":
      "The recording was saved locally. Reconnect to the internet and then upload it.",
    "uploadFailed":
      "The recording was saved locally, but the upload failed. You can try again.",
    "uploadInterrupted":
      "The previous upload was interrupted. You can try again.",
  },
  "recordings": {
    "actions": {
      "cancelPending": "Cancel pending requests",
      "copied": "Copied!",
      "copyChunk": "Copy part {{index}}",
      "copyChunks": "Copy documentation in parts",
      "copyComplex": "Copy “{{name}}”",
      "copyDocumentation": "Copy documentation",
      "copyEmpty": "No content to copy.",
      "copyFetchFailed": "Could not load content.",
      "copyImprovedOriginal": "Copy improved version",
      "copySummary": "Copy summary",
      "deleteRecording": "Delete recording",
      "document": "Document",
      "downloadDocumentation": "Download documentation",
      "downloadEmpty": "No content to download.",
      "downloadFetchFailed": "Could not load content.",
      "downloadImprovedOriginal": "Download improved version",
      "drawerTitle": "Actions",
      "markPending": "Mark as pending",
      "markProcessed": "Mark as processed",
      "more": "More actions",
      "nextPending": "Next pending recording",
      "processing": "processing...",
      "reprocess": "Reprocess",
      "showAiRequests": "Show AI requests",
    },
    "aiGenerated": "AI-generated",
    "appendAudio": {
      "appendModeHeaderTitle": "Add audio",
      "fieldsLockedHint": "From the original recording",
      "focusHint": "This recording will be added to “{{name}}”.",
      "focusHintUntitled":
        "This recording will be added to the existing recording.",
      "queueItemTooltip": "Will be added to this recording",
      "transcriptionPending": "Please wait until transcription is complete.",
      "trigger": "Add audio",
      "uploadFailedFallback": "Audio could not be added.",
    },
    "audio": {
      "adjustRetention": "Adjust retention settings",
      "deleted":
        "The audio recording was automatically deleted after the configured retention period.",
      "deletedShort": "Deleted",
      "playerLabel": "Audio recording: {{name}}",
      "quality": "Quality",
      "title": "Audio",
      "unavailable": "Audio is no longer available.",
    },
    "audioPlayer": {
      "download": "Download",
      "forward": "Skip forward 15 seconds",
      "pause": "Pause",
      "play": "Play",
      "rewind": "Rewind 15 seconds",
      "speed": "Playback speed",
    },
    "audioQuality": {
      "fair": "Audio quality: fair",
      "flags": {
        "clipped": "Too loud in places",
        "distant_mic": "Microphone far away",
        "long_dead_air": "Long stretches without speech",
        "mic_left_on": "Microphone left running",
        "mic_too_close": "Microphone too close",
        "narrowband": "Muffled sound",
        "no_speech": "No speech detected",
        "recording_mostly_empty": "Recording mostly empty or broken",
      },
      "good": "Audio quality: good",
      "level": {
        "fair": "fair",
        "good": "good",
        "poor": "poor",
        "unknown": "unknown",
        "unusable": "unusable",
      },
      "poor": "Audio quality: poor",
      "title": "Audio quality",
      "unknown": "Audio quality: unknown",
      "unusable": "Audio quality: unusable",
    },
    "billing": {
      "billingViewLabel": "Billing view",
      "forInsurance": "Billing for",
      "reviewedBadge": "Reviewed",
      "reviewSuggestions": "Review AI suggestions",
    },
    "billingCheck": {
      "and": "and",
      "approx": "approx. {{amount}}",
      "badgeNotBilled_one": "{{count}} not billed",
      "badgeNotBilled_other": "{{count}} not billed",
      "badgeNotDocumented_one": "{{count}} not documented",
      "badgeNotDocumented_other": "{{count}} not documented",
      "badgePending": "Bill not yet confirmed in Teemer",
      "billedInTeemer": "Billed in Teemer, {{times}}×",
      "billedInTeemer_one": "",
      "billedInTeemer_other": "",
      "collapse": "Collapse",
      "collapsedOk_one": "{{count}} without differences",
      "collapsedOk_other": "{{count}} without differences",
      "disclaimer":
        "AI-assisted analysis. Please check each case. Dentero accepts no liability for its accuracy. The billing decision stays with the practice.",
      "estimate": "estimated {{range}}",
      "expand": "Expand",
      "filter": {
        "all": "All",
        "findings": "With findings",
        "notBilled": "Documented, not billed",
        "notDocumented": "Billed, not documented",
        "ok": "No differences",
        "pending": "Not yet confirmed in Teemer",
      },
      "howItWorks": "How does this work?",
      "inDocumentation": "In the documentation:",
      "insurance": {
        "private": "Private (PKV)",
        "public": "Statutory (GKV)",
      },
      "inTreatments_one": "in {{count}} treatment",
      "inTreatments_other": "in {{count}} treatments",
      "nextStepNotBilled":
        "Check whether the service can be added to the bill in Teemer.",
      "nextStepNotDocumented":
        "This service is not in the documentation. Add it to the documentation or check the bill.",
      "noFindings": "No differences from the Teemer bill found.",
      "notBilledHeading": "Documented, but not billed",
      "notDocumentedHeading": "Billed, but not documented",
      "notDocumentedInline": "billed in Teemer, not in the documentation",
      "period": "{{start}} to {{end}}",
      "range": "{{min}} to {{max}}",
      "summarySub":
        "Your documentation compared with the confirmed Teemer bill",
      "summarySubWithPeriod":
        "Your documentation compared with the confirmed Teemer bill, {{period}}",
      "tab": "Billing check",
      "tabHeadline_one":
        "{{count}} difference between documentation and Teemer bill",
      "tabHeadline_other":
        "{{count}} differences between documentation and Teemer bill",
      "tabSub":
        "Dentero compared the documentation with the bill imported from Teemer on {{date}}.",
      "tabSubUndated":
        "Dentero compared the documentation with the bill imported from Teemer.",
      "tile": {
        "notBilled": "Documented, but not billed",
        "notDocumented": "Billed, but not documented",
        "ok": "No differences",
        "okDetail": "Documentation and bill match",
      },
      "title": "Billing check",
      "tooth": "Tooth {{teeth}}",
      "upgradeBox":
        "See these differences before you bill. Billing suggestions are included in the {{tier}} package.",
      "upgradeLine":
        "With billing suggestions you see these differences before you bill. They are included in the {{tier}} package.",
      "upgradeLink": "More about billing suggestions",
    },
    "billingRequest": {
      "creatingDescription": "The billing suggestion is being created.",
      "creatingTitle": "Creating...",
      "errorDescription":
        "The suggestion could not be processed. Please try again.",
      "errorTitle": "An error occurred",
      "generate": "Create billing suggestion",
      "initialDescription":
        "Create billing suggestions for the currently selected insurance type.",
      "initialPrivate":
        "AI will suggest billing positions for privately insured patients from GOZ and GOÄ.",
      "initialPublic":
        "AI will suggest billing positions for publicly insured patients from BEMA, GOZ, and GOÄ.",
      "initialTitle": "Create billing suggestions",
      "preparingDescription":
        "The suggestion will start automatically once the improved version is ready.",
      "preparingTitle": "Preparing...",
    },
    "chat": {
      "button": "Ask the AI",
      "clear": "Clear history",
      "clearConfirmDescription":
        "Are you sure you want to clear the chat history for this recording? This cannot be undone.",
      "clearConfirmTitle": "Clear chat history?",
      "close": "Close",
      "disclaimer":
        "You are interacting with an AI system. The assistant helps with the documentation and billing of this recording.",
      "empty":
        "Ask a question about this recording, e.g. why a billing position was suggested.",
      "errors": {
        "empty": "Please enter a message.",
        "generic": "The answer could not be generated. Please try again.",
      },
      "noMedicalAdvice": "Not medical advice.",
      "placeholder": "Ask a question about this recording ...",
      "send": "Send",
      "title": "Ask the AI",
      "tools": {
        "fetch_ai_reasoning": "Read AI reasoning",
        "fetch_billing_positions": "Read billing positions",
        "fetch_combinations": "Read combinations",
        "fetch_complexes": "Read practice complexes",
        "fetch_custom_prompts": "Checked custom prompts",
        "fetch_documentation": "Read documentation",
        "fetch_dzr_h1": "Queried DZR H1 PRO",
        "fetch_exclusions": "Read exclusions",
        "fetch_justifications": "Read justifications",
        "fetch_transcription": "Read transcription",
        "web_search": "Searched the web",
      },
    },
    "complexesBadge": {
      "label_one": "{{count}} complex applied",
      "label_other": "{{count}} complexes applied",
    },
    "complexTemplates": {
      "choiceHint": "Select one or more alternatives.",
      "conditionDoesNotApply": "Does not apply",
      "conditionFalse": "Left out: {{label}}",
      "conditionTrue": "Applies: {{label}}",
      "edit": "Edit text",
      "editCancel": "Cancel",
      "editDiscard": "Discard edit",
      "edited": "Edited by hand",
      "editSave": "Save",
      "evidence": "Evidence",
      "fillPlaceholder": "Enter a value",
      "generate": "Fill complex",
      "generateDescription":
        "AI fills this template from the improved documentation.",
      "listHint": "One point per line.",
      "rerenderedSinceEdit":
        "A later dictation filled the template again. Discard your edit to use the new text.",
      "slotMissing": "Open: {{label}}",
      "slotOptions": "Alternatives: {{options}}",
      "stale":
        "The complex changed since this version. Reprocess to use the current template.",
    },
    "controls": {
      "clickToRecord": "Click to record",
      "discard": "Discard recording",
      "listenRecording": "Listen to recording",
      "microphoneUnavailable": "Microphone unavailable",
      "paused": "Paused — listen, resume, or save",
      "playingPreview": "Playing...",
      "processShort": "Save",
      "recording": "Recording — click to pause",
      "recordingFinished": "Recording finished — save or discard",
      "saveAndProcess": "Save recording and process",
      "saveShort": "Save",
      "starting": "Starting recording",
      "stopPreview": "Stop playback",
    },
    "details": {
      "liveTake": {
        "awaiting":
          "The live chart is placed on the patient's chart once the patient number is entered.",
        "awaitingShort": "Waiting for the patient number",
        "check":
          "This visit's findings are still on patient {{number}}'s chart, because that chart changed since. Please check it there.",
        "checkShort": "Also check patient {{number}}'s chart",
        "confirm":
          "The live chart is not placed yet. Check that {{number}} is the patient number and save it.",
        "confirmShort": "Confirm the patient number to place the chart",
      },
      "openAuditTrail": "Open audit trail",
      "openPatientRecord": "Open patient record",
      "patientNumberDictated": "from the dictation",
      "saveChangeDescription":
        "This line in the structured documentation will be updated. The rest of the text stays:",
      "saveChangeTitle": "Save changes?",
      "source": {
        "api": "Recorded in app",
        "liveRecording": "Recorded in browser",
        "uploadLink": "Uploaded via link",
      },
      "treaterAccessTransfer":
        "Access to audio and transcript moves to the new treater; you may lose your own access",
      "treaterChangeDescription":
        "Do you want to change the treater for this recording?",
      "treaterChangeTitle": "Change treater?",
      "treaterPlaceholder": "Select treater",
      "treaterPrivacyDescription":
        "With privacy mode on, changing the treater also changes access:",
      "updatedPatientIdInDocumentation": "Patient ID",
      "updatedTreaterInDocumentation": "Treater",
      "updatedTreatmentDateInDocumentation": "Treatment date",
      "via": "via",
    },
    "documentation": {
      "adjust": "Adjust documentation",
      "adjustShort": "Adjust",
      "editPlaceholder": "Edit documentation...",
      "rerender": "Recreate",
      "rerenderConfirm":
        "AI recreates the structured documentation with the included templates. Your manual edits will be lost.",
      "rerenderTitle": "Recreate structured documentation?",
      "saving": "Saving...",
      "templatesChanged":
        "Templates changed. Recreate the structured documentation to include the changes.",
      "title": "Documentation",
    },
    "drafts": {
      "description": "Recordings that still need to be uploaded.",
      "duration": "Recording duration",
      "notSet": "Not set",
      "onlineOnly": "Online only",
      "recordedAt": "Recording date",
      "remove": "Remove recording",
      "removeDialog": {
        "confirm": "Remove",
        "description":
          "This local recording and all associated data will be removed. This action cannot be undone.",
        "title": "Remove recording?",
      },
      "retry": "Upload again",
      "status": {
        "actionNeeded": "Action needed",
        "interrupted": "Interrupted",
        "ready": "Ready",
        "uploading": "Uploading",
      },
      "title": "Upload queue",
      "upload": "Upload",
      "uploadAlreadyRunning": "Upload already in progress",
    },
    "export": {
      "button": "Export",
      "exporting": "Exporting…",
    },
    "filters": {
      "allSuggestions": "All",
      "withoutSuggestions": "Without suggestions",
      "withSuggestions": "With suggestions",
    },
    "form": {
      "patientIdHint":
        "You can also speak the patient ID as part of the recording; it is used for assignment in the PVS.",
      "selectDate": "Select date",
    },
    "improvedOriginal": {
      "complexDetected": "Complex detected. Apply it below if it belongs.",
      "complexesDisabled": "Complexes cannot be changed while processing.",
      "failedDescription":
        "Please try again. If the error persists, contact support.",
      "noAppliedComplexes":
        "Complexes are active for this recording, but no complexes were applied in the current version.",
      "previousComplexes": "Previously applied complexes",
      "reprocess": "Reprocess",
      "title": "Improved version",
    },
    "index": {
      "batch": {
        "clearSelection": "Clear selection",
        "deleteDialog": {
          "description":
            "This action cannot be undone. All selected documentation and their data will be permanently deleted.",
          "title_one": "Delete {{count}} documentation?",
          "title_other": "Delete {{count}} documentation records?",
        },
        "deleteSelected": "Delete selected",
        "markPending": "Mark as pending",
        "markProcessed": "Mark as processed",
        "selectAll": "Select all",
        "selectAllPage": "Select all on this page",
        "selected": " selected",
      },
      "liveView": "Live view",
      "markAs": "Mark as {{status}}",
      "poolCount": {
        "label": "Documentation pool",
        "usedOfLimit": "{{used}} / {{limit}}",
      },
      "searchPlaceholder": "Search by patient ID, keywords, or description",
      "selection": {
        "selectRecording": "Select {{name}}",
      },
    },
    "ivoris": {
      "lookupMissingData": "Patient ID or treatment date missing for lookup.",
      "notFound": "No Ivoris patient found for",
      "noTreatmentDate": "No treatment date",
      "notSynchronised": "Not synchronized",
      "patientFound": "Patient found",
      "patientId": "Patient ID: {{value}}",
      "requiresStructuredDocumentation":
        "Structured documentation must be complete.",
      "resetStatus": "Reset status",
      "syncDocumentation": "Transfer documentation",
      "synchronised": "Synchronized",
      "treatmentDate": "Treatment date: {{value}}",
    },
    "materialAnnotations": {
      "catalogNumber": "Catalog number: {{number}}",
      "notInCatalog": "Not in catalog",
      "open_one": "Open material details",
      "open_other": "Open {{count}} material details",
      "title": "Detected material",
    },
    "metadata": {
      "audioTooltip": "Audio duration and audio quality",
      "billing": {
        "failed": "failed",
        "laboratoryServices_one": "{{count}} laboratory service",
        "laboratoryServices_other": "{{count}} laboratory services",
        "pending": "creating",
        "positions_one": "{{count}} position",
        "positions_other": "{{count}} positions",
      },
      "createdAtTooltip": "Created at",
      "createdBy": "Created by",
      "liveTakeAwaiting": "Live chart is waiting for a patient number",
      "liveTakeNeedsCheck": "Check the chart of another patient",
      "openPatient": "Open patient {{number}}",
      "patientIdNotSet": "Patient ID not set",
    },
    "mobileSections": {
      "audio": "Audio",
      "billing": "Billing",
      "chart": "Tooth status",
      "details": "Details",
      "improved": "Improved version",
      "structured": "Documentation",
    },
    "new": {
      "cockpit": {
        "assistantChange": "Change",
        "assistantPanelTitle": "Assistant",
        "captureSectionDescription":
          "Microphone, complexes, and live transcript for this recording.",
        "captureSectionTitle": "Recording setup",
        "compactQueued_one": "{{count}} upload waiting",
        "compactQueued_other": "{{count}} uploads waiting",
        "complexesPopoverTitle": "Apply complexes",
        "complexesShort": "Complexes",
        "complexesShortWithCount_one": "Complex ({{count}})",
        "complexesShortWithCount_other": "Complexes ({{count}})",
        "contextSectionDescription":
          "These details help match the recording in the PVS.",
        "contextSectionTitle": "Documentation context",
        "controls": {
          "idleState": "Ready",
          "pause": "Pause",
          "pausedState": "Paused",
          "readyState": "Ready",
          "record": "Record",
          "recordingState": "Live",
          "resume": "Resume",
          "resumeRecording": "Resume recording",
        },
        "dateUnset": "Select date",
        "detailsDescription": "Practice, treater, and local uploads.",
        "detailsTitle": "Recording details",
        "detailsTrigger": "Details",
        "dividerAria": "Resize sections",
        "draftsTriggerAria_one": "Open upload queue, {{count}} recording",
        "draftsTriggerAria_other": "Open upload queue, {{count}} recordings",
        "editLockedWhileRecording": "Cannot be edited while recording",
        "focus": {
          "enableLiveCta": "Enable live transcript",
          "idleDescription":
            "Start the recording from the controls below. Adjust details above.",
          "idleTitle": "Ready to record",
          "pausedDescription":
            "Listen to the recording, resume it, or save it below.",
          "pausedTitle": "Recording paused",
          "recordingDescription":
            "Recording is in progress. Enable the live transcript to see speech in realtime.",
          "recordingTitle": "Recording in progress",
        },
        "headerTitle": "New recording",
        "lowerPaneAria": "Lower working pane",
        "microphoneShort": "Mic",
        "patientIdPlaceholder": "123…",
        "patientIdUnset": "no ID",
        "patientUnset": "Patient not set",
        "queueSheet": {
          "empty": "No locally saved recordings.",
        },
        "realtimeInactive": "Off",
        "realtimeShort": "Transcription",
        "segmented": {
          "assistant": "Assistant",
          "recording": "Recording",
          "transcript": "Transcript",
        },
        "setup": {
          "assignment": "Assignment",
          "assistantRow": "Documentation assistant",
          "capture": "Recording",
          "drafts": "Drafts",
          "off": "Off",
          "on": "On",
          "ready": "Ready to record",
          "realtimeFootnote":
            "The text appears while you record. The audio recording is kept as a fallback.",
          "recording": "Recording",
          "save": "Save recording",
          "title": "Record",
        },
        "setupDescription": "Microphone, complexes, and live transcript.",
        "setupTitle": "Recording setup",
        "setupTrigger": "Setup",
        "takeSettings": {
          "open": "Settings",
          "patientLocked":
            "Cannot be changed once the chart has started. The chart belongs to this patient.",
          "title": "Settings",
        },
        "transcriptSheet": {
          "description":
            "The live transcript of this recording. Recording continues.",
          "open": "Transcript",
          "title": "Transcript",
        },
        "treaterPlaceholder": "Select treater",
        "viewSwitch": {
          "assistantBadge": "{{name}} · {{current}}/{{total}}",
          "chartFailedNotice":
            "The live chart was discarded. The recording continues. The chart is created from the transcript after you stop.",
          "chartTickFailedNotice":
            "The tooth status could not be updated right now. The recording continues.",
          "dismissNotice": "Dismiss notice",
          "hint": "Both keep running, even when hidden.",
        },
      },
      "complexes": {
        "active": "Complexes active",
        "armedStripTitle": "Active complexes",
        "inactive": "Complexes inactive",
        "infoAriaLabel": "Show explanation of complexes",
        "switchAriaLabel": "Apply complexes for this recording",
        "tooltip":
          "By speaking trigger words, you can integrate complexes into the documentation.",
      },
      "discardDialog": {
        "confirm": "Discard recording",
        "description": "Do you really want to discard the current recording?",
        "title": "Discard recording?",
      },
      "documentationAssistant": {
        "befundBlocked":
          "Not available in the 01 findings chart. Turn off Live transcript to use a template.",
        "chooseTemplate": "Choose template",
        "chooseTemplateHint":
          "The selected template appears below the live transcript.",
        "clearTemplate": "Clear template",
        "description":
          "Shows the assistant for structured documentation during recording.",
        "label": "Assistant",
        "selectedTemplate": "Selected: {{name}}",
        "switchAriaLabel": "Show the assistant for this recording",
      },
      "interrupted": {
        "description":
          "Audio recorded so far is kept locally. You can save or discard it now.",
        "title": "Recording was interrupted",
      },
      "leaveDialog": {
        "confirm": "Discard and leave",
        "title": "Discard recording?",
      },
      "microphone": {
        "stopTest": "Stop test",
        "test": "Test",
      },
      "noAudio": {
        "description":
          "No audio is being captured. Check that the correct microphone is selected and not muted.",
        "kept": "The recording so far is kept.",
        "running": "Recording continues.",
        "tabTitle": "NO AUDIO!",
        "title": "No sound detected from your microphone",
      },
      "noSpeech": {
        "notice": "No speech detected. Recording continues.",
      },
      "offline": {
        "badge": "Queue paused",
        "description":
          "New recordings go to the queue until connection is restored.",
        "title": "Offline",
      },
      "realtimeTranscription": {
        "acknowledgement": {
          "confirm": "Enable Live Transcript",
          "description":
            "The audio signal is transcribed live while you record. When you save, the live transcript becomes the basis for documentation and billing. The audio recording remains available as a safe fallback.",
          "title": "Document live — without waiting for processing",
        },
        "audioStillRecording":
          "Audio recording continues normally and will be transcribed when you save.",
        "connecting": "Connecting live transcript…",
        "connectionState": {
          "connected": "Connected",
          "connecting": "Connecting…",
          "error": "Error",
          "idle": "Idle",
          "paused": "Paused",
          "reconnecting": "Restoring connection…",
          "stopping": "Finalizing transcript…",
        },
        "emptyPlaceholder":
          "Your live transcript will appear here as soon as you speak.",
        "error":
          "Live transcript could not connect. Recording continues. Please enable it again or try later.",
        "errors": {
          "finalizationFailed":
            "The live transcript could not be finalized completely. The audio recording will be used automatically when you save.",
          "noMicrophoneStream":
            "No microphone signal is currently available for the live transcript.",
          "sessionExpired":
            "The maximum live transcription duration was reached. Audio recording continues.",
          "signedOut":
            "You were signed out. Sign in again in a new tab. The audio recording continues, but the live transcript stays off.",
          "tokenFetchFailed":
            "Live transcript could not authenticate. Recording continues. Please enable it again or try later.",
          "transcriptionFailed":
            "Live transcript could not connect. Recording continues. Please enable it again or try later.",
          "unavailable":
            "Live transcription unavailable. Recording continues and will be transcribed after saving.",
          "unavailableBefund":
            "The Befund is created from the recording after saving.",
        },
        "errorTitle": "Live transcript",
        "live": "Live",
        "micMuted": "Microphone muted — no speech is being captured right now.",
        "paused": "Live transcript paused",
        "qualityHint":
          "For better results: keep recordings short, reduce background noise, and keep the microphone close.",
        "scrollToLatest": "Jump to latest",
        "sourceOfTruth": "Saved as the basis for documentation and billing.",
        "speakerLabel": "Speaker {{speaker}}",
        "states": {
          "connected": "Live",
          "connecting": "Connecting",
          "error": "Connection interrupted",
          "idle": "Ready",
          "paused": "Paused",
          "reconnecting": "Restoring connection",
          "stopping": "Finalizing transcript",
        },
        "title": "Live transcript",
        "toggleLabel": "Realtime transcription",
        "unavailable":
          "Realtime transcription is not available for this practice.",
        "waitingForSpeech": "Waiting for speech…",
      },
      "silentSaveDialog": {
        "confirm": "Save anyway",
        "description":
          "No audio was detected in this recording. It will be saved as “No speech recognized” and will not be transcribed. Check the microphone or save anyway.",
        "title": "No audio in this recording",
      },
      "tabs": {
        "recording": "Recording",
      },
      "title": "New documentation",
      "treater": "Treater",
      "workflow": {
        "befund": "01 findings",
        "befundHeaderTitle": "Capture 01 findings",
        "chartStageAnalyzingDescription":
          "Checking the transcript chunk for chart changes.",
        "chartStageAnalyzingTitle": "Analyzing findings…",
        "chartStageConfirm": "Confirm",
        "chartStageDiscardAll": "Discard all",
        "chartStageEmptyDescription":
          "Teeth without an entry are unexamined. Tap a tooth or surface for manual corrections.",
        "chartStageEmptyDiff": "No changes proposed yet.",
        "chartStageIdleDescription":
          "Start recording to propose chart changes from speech.",
        "chartStageIdleTitle": "Odontogram ready",
        "chartStageListeningDescription":
          "Spoken findings will appear as proposals once analysis is available.",
        "chartStageListeningTitle": "Recording in progress",
        "chartStageLoadingDescription": "Loading the current dental chart.",
        "chartStageLoadingTitle": "Loading chart…",
        "chartStageManualBadge": "manual",
        "chartStageNeedsPatientDescription":
          "A valid patient number is required for 01 findings capture.",
        "chartStageNeedsPatientTitle": "Patient number missing",
        "chartStageOfflineDescription":
          "01 findings requires an online connection with live transcription.",
        "chartStageOfflineTitle": "Offline",
        "chartStagePendingCount_one": "{{count}} change",
        "chartStagePendingCount_other": "{{count}} changes",
        "chartStagePendingTitle": "Pending proposals",
        "chartStageProposalLabel": "Proposal {{index}} · {{opCount}} ops",
        "chartStageStaleError":
          "The chart was changed elsewhere. Please reload.",
        "chartStageTitle": "Odontogram",
        "chartStageUndo": "Undo",
        "offlineUnsupported":
          "01 findings requires an online connection with live transcription.",
        "patientLater":
          "Optional. Without a number, the chart is placed once it is entered.",
        "realtimeRequired": "The 01 findings chart needs live transcription",
        "selectorAria": "Select recording mode",
        "treatment": "Documentation",
      },
    },
    "patientHistory": {
      "button": "Patient history",
      "close": "Close",
      "empty": "No other treatments for this patient.",
      "loadError": "The patient history could not be loaded. Please try again.",
      "recordedAt": "{{value}}",
      "title": "Patient history",
    },
    "plausibility": {
      "allCovered":
        "No critical gaps – all information required for billing and evidencing is documented.",
      "appendPane": {
        "description":
          "The plausibility check flagged these points as open. Dictate the missing information – it will be merged into the documentation after processing.",
        "tab": "Open points",
      },
      "coveredHeading_one": "{{count}} covered point",
      "coveredHeading_other": "{{count}} covered points",
      "description":
        "This information is missing from the documentation although it is required for billing or as evidence towards insurer and patient.",
      "feedback": {
        "down": "Not helpful",
        "failed": "The rating could not be saved.",
        "question": "Was this check helpful?",
        "up": "Helpful",
      },
      "headerBadge_one": "{{count}} critical documentation gap",
      "headerBadge_other": "{{count}} critical documentation gaps",
      "indexBadge": "Critical documentation gaps",
      "nonCriticalHeading_one": "{{count}} further note (not critical)",
      "nonCriticalHeading_other": "{{count}} further notes (not critical)",
      "notApplicableHeading_one": "{{count}} not applicable point",
      "notApplicableHeading_other": "{{count}} not applicable points",
      "status": {
        "covered": "documented",
        "missing": "missing",
        "not_applicable": "not applicable",
        "partial": "incomplete",
      },
      "tab": "Plausibility",
      "title": "Open documentation points",
    },
    "privacy": {
      "protectedContent":
        "Privacy protection is active. This recording does not belong to the currently logged-in user.",
      "revealButton": "Show anyway",
    },
    "report": {
      "alreadyReported": "A report was already sent on {{at}}.",
      "description": "Please enter a reason for the report.",
      "submit": "Submit report",
      "title": "Report recording",
      "trigger": "Report",
    },
    "requestEmpty": {
      "description": "Content will be available shortly.",
      "title": "Creating...",
    },
    "show": {
      "shortSummaryPlaceholder": "Enter short description",
      "treatmentSummaryPlaceholder": "Enter summary",
    },
    "status": {
      "noRecording": "No recording",
      "pending": "Pending",
      "processed": "Processed",
      "unprocessable": "Not processed",
    },
    "statusTitle": {
      "correctionRunning": "Checking transcript…",
      "failed": "Processing failed",
      "noName": "No title found",
      "notProcessed": "Not processed yet",
      "processing": "Processing...",
      "transcriptionRetrying":
        "Transcription is taking longer than usual, retrying...",
      "transcriptionRunning": "Transcription running...",
      "unprocessable": {
        "audio_too_long": "Recording too long (max. 5 hours)",
        "default": "Couldn't be processed",
        "no_speech": "No speech detected",
        "unreliable_transcription": "Unreliable transcription",
      },
    },
    "storagePrune": {
      "cancel": "Cancel and manage manually",
      "confirm": "Delete oldest recordings",
      "description_one":
        "Local storage is almost full, so a new recording can't be started. Delete the oldest recording waiting to upload in the queue ({{size}}) to free space, or cancel and manage the queue yourself.",
      "description_other":
        "Local storage is almost full, so a new recording can't be started. Delete the {{count}} oldest recordings waiting to upload in the queue ({{size}}) to free space, or cancel and manage the queue yourself.",
      "title": "Storage almost full",
    },
    "structuredDocumentation": {
      "notAvailable": "Not available",
      "processingError": "Processing error",
    },
    "transcription": {
      "adjust": "Adjust transcription",
      "confirmDialog": {
        "discardConfirm": "Discard",
        "discardDescription":
          "Your changes to the transcription have not been saved yet.",
        "modelLabel": "Transcription model",
        "overwrites": {
          "billing": "Billing suggestions",
          "documentation": "Documentation",
          "improved": "Improved version",
        },
        "retranscribeDescription":
          "This recording has already been processed. If you continue, the following data will be overwritten:",
        "saveDescription":
          "The edited text will be saved as transcription (raw version) and replace the previous content of this field.",
        "title": "Continue and overwrite?",
      },
      "correcting": "Checking transcript…",
      "mergeFailed": "Audio could not be merged",
      "merging": "Merging audio...",
      "modes": {
        "edit": "Edit",
        "read": "Read",
      },
      "reset": "Reset",
      "retranscribe": "Retranscribe",
      "retranscriptionRequest": {
        "description":
          "We run retranscriptions for you. Briefly describe what is wrong with the current transcription and our support team will get back to you.",
        "placeholder":
          "e.g. the dental terms were misrecognised, the wrong treatment was recorded ...",
        "submit": "Send request",
        "title": "Request a retranscription",
        "trigger": "Request new transcription",
      },
      "retrying": "Transcription is taking longer than usual, retrying...",
      "running": "Transcription running...",
      "title": "Transcription",
    },
    "transferWindow": {
      "active": "Transfer window active",
      "coachmark": {
        "description":
          "Open a separate transfer window with documentation records. Search for cases and copy text into the PVS faster.",
        "dismiss": "Got it",
        "openNow": "Open now",
        "title": "Transfer to PVS faster",
      },
      "open": "Open transfer window",
      "title": "Transfer mode",
      "tooltip": "Opens a reduced view for quick copying into the PVS",
    },
  },
  "recordingSession": {
    "captureRecovered":
      "The microphone dropped out. Recording continues automatically — the part recorded so far is saved in the queue.",
    "discardedLocalRecording": "Local recording discarded.",
    "longRecordingWarning":
      "This recording has been running for a while. Remember to save it.",
    "maxDurationReached":
      "The maximum recording length of {{hours}} hours was reached. The recording was saved automatically.",
    "prepareFailed": "The recording could not be prepared locally.",
    "saveFailed":
      "The recording could not be saved. It is in the queue and can be uploaded again.",
    "saveFailedNotQueued":
      "The recording could not be saved. Please try again before leaving this page.",
    "segmentsMissing":
      "Part of the recording was no longer stored locally and is missing from the saved recording.",
    "unsavedBeforeUnload":
      "The current recording has not been saved yet. If you leave this page, the recording will be lost.",
    "unsavedNavigation":
      "The current recording has not been saved yet. Do you want to discard it and leave this page?",
  },
  "regulationPositions": {
    "billingDimensions": {
      "cardinalities": {
        "once_per_locator": "Once per locator",
        "once_per_treatment": "Once per treatment",
        "repeatable_per_locator": "Repeatable per locator",
        "repeatable_per_treatment": "Repeatable per treatment",
      },
      "cardinality": "Cardinality",
      "earliestAfterMonths": "Earliest after (months)",
      "groupable": "Groupable",
      "locator": "Locator",
      "locators": {
        "none": "None",
        "teeth_region": "Teeth region",
        "tooth": "Tooth",
        "tooth_surfaces": "Tooth + surfaces",
      },
      "maxCount": "Max. count",
      "noTimeframe": "No frequency limit",
      "requiresSurfaces": "Requires surfaces",
      "source": "Source",
      "timeframeWindow": "Frequency window",
      "title": "Billing dimensions",
      "unit": "Billing unit",
      "units": {
        "cavity": "Cavity",
        "implant": "Implant",
        "jaw": "Jaw",
        "jaw_section": "Jaw section",
        "operation_site": "Operation site",
        "restoration": "Restoration",
        "root_canal": "Root canal",
        "service": "Service",
        "session": "Session",
        "span": "Span",
        "tooth": "Tooth",
      },
    },
    "card": {
      "addToBillingSuggestions": "Add to billing suggestions",
      "emptyWithTitle": "No {{title}} available",
    },
    "details": {
      "amount": "Amount",
      "category": "Category",
      "noCategory": "No category",
      "noTitle": "No title",
      "select": "Select",
      "short": "Short form",
    },
    "dialog": {
      "excludedPositions": "Excluded positions:",
      "loadingDetails": "Loading more details...",
    },
    "drawer": {
      "combinations": "Combinations",
      "exclusions": "Exclusions",
    },
    "form": {
      "createAction": "Create",
      "createTitle": "Create new billing position",
      "description":
        "Create a new billing position. After creation you can edit all details.",
      "positionPlaceholder": "e.g. 0010, Ä1, 01",
      "typePlaceholder": "Select type",
    },
    "index": {
      "add": "Add",
      "combinationsShort": "Comb.",
      "exclusionsShort": "Excl.",
      "justificationsShort": "Just.",
      "points": "Points",
      "position": "Position",
      "searchPlaceholder": "Search by code or title",
      "type": "Type",
    },
    "justifications": {
      "description": "Justifications are used to explain factor increases.",
      "empty": "No justifications",
      "placeholder": "Enter new justification...",
      "title": "Justifications",
    },
    "relations": {
      "loading": "Loading...",
      "placeholder": "Select position...",
    },
    "show": {
      "archiveAction": "Archive",
      "archiveDescription":
        "Archived positions are hidden from suggestions and lists. This action can be undone.",
      "archiveTitle": "Archive position?",
      "combinations": "Combinations",
      "deleteDescription":
        "The position will be permanently deleted from the database. All linked combinations and exclusions will also be removed.",
      "deleteTitle": "Delete position?",
      "exclusions": "Exclusions",
      "noCombinations": "No combinations",
      "noExclusions": "No exclusions",
      "restoreAction": "Restore",
      "restoreDescription":
        "The position will be active again and shown in all lists.",
      "restoreTitle": "Restore position?",
    },
  },
  "systemMessages": {
    "banner": {
      "defaultTitle": "New update",
      "destructiveTitle": "Important notice",
      "learnMore": "Learn more",
      "showLess": "Show less",
      "showMore": "Show more",
      "warningTitle": "Warning",
    },
    "form": {
      "createAction": "Create",
      "editDescription": "Edit the system message.",
      "editTitle": "Edit system message",
      "linkLabel": "Link URL (optional)",
      "linkPlaceholder": "https://support.dentero.app/...",
      "messageLabel": "Message",
      "messagePlaceholder": "Enter message...",
      "newDescription": "Create a new system message for all users.",
      "newTitle": "New system message",
      "typeLabel": "Type",
    },
    "index": {
      "empty": "No system messages yet.",
      "newMessage": "New message",
      "table": {
        "actions": "Actions",
        "createdAt": "Created",
        "message": "Message",
        "status": "Status",
        "type": "Type",
      },
    },
    "row": {
      "active": "Active",
      "archive": "Archive",
      "confirmDelete": "Really delete system message?",
      "copy": "Copy",
      "edit": "Edit",
      "publish": "Publish",
    },
    "statuses": {
      "archived": "Archived",
      "draft": "Draft",
      "published": "Published",
    },
    "variants": {
      "default": "Info",
      "destructive": "Error",
      "warning": "Warning",
    },
  },
  "teemer": {
    "billing": {
      "clickToSync": "Click to transfer",
      "countMismatch":
        "Teemer booked {{actual}} services instead of {{expected}}. Please check in Teemer.",
      "countNotFound":
        "Teemer booked none of the {{expected}} services. Please check in Teemer.",
      "empty": "No services",
      "incompleteHelp":
        "Tooth or surfaces are missing. Teemer will flag the service as incomplete.",
      "resetStale": "Reset stalled transfer",
      "resetSync": "Reset transfer status",
      "syncCount_one": "{{count}} service transferred",
      "syncCount_other": "{{count}} services transferred",
      "syncFailureHelp":
        "This service could not be synchronized to Teemer and must be added to the treatment manually.",
      "syncFailureSessionNotFound":
        "No matching Teemer session found. Check the session and try transferring again.",
      "syncRunning": "Transfer in progress...",
      "title": "Services",
      "warning":
        "Please verify billing suggestions for accuracy and completeness after transfer!",
    },
    "comparison": {
      "aligned": "Aligned",
      "denteroSide": "{{brand}}",
      "empty": "Neither side has positions for this insurance.",
      "onlyInDentero": "Only in {{brand}}",
      "onlyInTeemer": "Only in Teemer",
      "reason": "Reason",
      "status": "Match",
      "suggestionsTab": "Suggestions",
      "syncedWarning":
        "Our suggestions were already transferred to Teemer, so aligned codes may come from our own transfer.",
      "tab": "Teemer comparison",
      "teemerSide": "Teemer",
      "unmapped": "Unmapped",
    },
    "document": {
      "autoSync": {
        "failed": {
          "comment_rejected":
            "Automatic transfer failed: Teemer rejected the comment.",
          "connection_error":
            "Automatic transfer failed: Teemer could not be reached.",
          "generic": "Automatic transfer failed.",
          "partial": "Part {{posted}} of {{total}} was transferred.",
          "sessions_unavailable":
            "Automatic transfer failed: Teemer sessions could not be loaded.",
          "timeout":
            "Automatic transfer failed: Teemer did not respond in time.",
          "unauthorized":
            "Automatic transfer failed: Teemer rejected the login.",
        },
        "movedAfterSync":
          "The earlier comment is on a different patient or date in Teemer and has to be removed there.",
        "partialRepost":
          "A manual transfer sends every part again. {{posted}} of {{total}} were already sent.",
        "secondComment": "Sending again adds another comment in Teemer.",
        "skipped": {
          "already_synchronised":
            "Automatic transfer skipped: already sent manually.",
          "ambiguous_session":
            "Automatic transfer skipped: several Teemer sessions. Please choose a session.",
          "disabled": "Automatic transfer skipped: the setting is off.",
          "generic": "Automatic transfer skipped.",
          "missing_patient_id":
            "Automatic transfer skipped: no patient number.",
          "missing_treated_at":
            "Automatic transfer skipped: no treatment date.",
          "no_matching_session":
            "Automatic transfer skipped: no matching Teemer session. Send it manually later.",
          "session_not_found":
            "Automatic transfer skipped: the stored Teemer session is gone. Please choose a session.",
          "unprocessable":
            "Automatic transfer skipped: no usable documentation.",
        },
        "syncedAt": "Synchronized automatically on {{date}}",
      },
      "heading": "Documentation",
      "includedInStructuredDocumentation":
        "Already part of the structured documentation",
      "lastTransferredPrefix": "Last transferred:",
      "rememberSelection": "Remember selection for future treatments",
      "selectionLegend": "Content for the Teemer comment",
      "separateEntries": "Send each document as a separate entry",
      "structuredDocumentation": "Structured documentation",
    },
    "externalPositions": {
      "importedAt": "Imported: {{date}}",
      "importFailed":
        "Import permanently failed on {{date}}. The nightly import skips this session.",
      "notImported":
        "Billed positions have not been imported yet. They appear after the nightly import of a confirmed Teemer session.",
      "unmappedTooltip":
        "This Teemer position could not be mapped to a billing code.",
    },
    "noSessionFound": "No Teemer session found for",
    "patientId": "Patient ID: {{value}}",
    "productName": "Teemer",
    "sessionSection": {
      "heading": "Session",
      "multiCount_one": "{{count}} session",
      "multiCount_other": "{{count}} sessions",
      "staleWarning": "Session no longer valid, please select again.",
    },
    "sessionStartOnDate": "on {{date}} at {{time}}",
    "sessionStartTime": "at {{time}}",
    "sessionState": {
      "CHECKED": "Checked",
      "CONFIRMED": "Confirmed",
      "ENDED": "Ended",
      "STARTED": "Started",
    },
    "syncDialog": {
      "billingPart": " and services",
      "description": "Transfer documentation{{billingPart}} to Teemer",
      "title": "Synchronization",
    },
    "syncStatus": {
      "docPending": "pending",
      "docSynced": "synchronized",
      "failed": "Automatic Teemer transfer needs a manual sync",
      "needsSync": "Teemer synchronization pending",
      "partial": "Teemer partially synchronized",
      "partialDetail":
        "Teemer partially synchronized: documentation {{docStatus}}, services {{synced}} of {{total}} synchronized",
      "synced": "Teemer fully synchronized",
    },
    "treatmentDate": "Treatment date: {{value}}",
  },
  "teemerSessions": {
    "allTreaters": "All treaters",
    "appointments": {
      "arrived": "In the waiting room",
      "arrivedSince": "In the waiting room since {{time}}",
      "loading": "Loading appointments …",
      "scheduled": "Scheduled",
    },
    "documentDisabled": {
      "alreadyRecorded": "This session already has a recording.",
      "notPrimaryPractice":
        "Recordings always go to your primary practice, so you can't document in this practice.",
      "notStarted":
        "You can document once the treatment has started in Teemer.",
    },
    "duration": {
      "actual_one": "{{count}} min",
      "actual_other": "{{count}} min",
      "loading": "Loading duration",
      "planned_one": "{{count}} min planned",
      "planned_other": "{{count}} min planned",
    },
    "liveView": "Live view",
    "nextDay": "Next day",
    "noSessionsForDay":
      "No Teemer sessions or appointments found for this day.",
    "noSessionsForTreater":
      "No Teemer sessions or appointments found for the selected treater.",
    "previousDay": "Previous day",
    "refresh": "Refresh list",
    "sortNewestFirst": "Show newest first",
    "sortOldestFirst": "Show oldest first",
    "sortOrderToggle": "Toggle sort order",
  },
  "transcriptionBenchmarks": {
    "diff": {
      "added_one": "+{{count}} added",
      "added_other": "+{{count}} added",
      "legend": "Comparing {{left}} (baseline) against {{right}}.",
      "removed_one": "−{{count}} removed",
      "removed_other": "−{{count}} removed",
      "tooLarge":
        "Highlighted diff is skipped for these long transcripts ({{left}} vs. {{right}} words) to keep the page responsive.",
      "unchanged_one": "{{count}} unchanged",
      "unchanged_other": "{{count}} unchanged",
    },
    "index": {
      "empty": "No benchmark runs yet.",
      "manageSamples": "Manage samples",
      "newRun": "New run",
      "providers": "Providers",
      "recentRuns": "Recent runs",
      "recentRunsDescription":
        "The latest benchmark runs with status, providers, and best word error rate.",
      "title": "Transcription benchmark",
    },
    "metrics": {
      "cost": "Cost",
      "duration": "Duration",
      "noReference": "No reference",
      "ratio": "Ratio",
      "tooltips": {
        "cost": "Estimated provider cost to transcribe this audio clip.",
        "duration": "Wall-clock time the provider took to transcribe the clip.",
        "ratio":
          "Processing time relative to audio length. Below 1× is faster than real-time; lower is better.",
        "wer":
          "Word Error Rate — share of words that differ from the reference transcript (ignoring case, punctuation, and number formatting). Lower is better.",
      },
      "wer": "WER",
    },
    "new": {
      "addComparison": "Add comparison",
      "advancedSettings": "Advanced settings",
      "configurationA": "Configuration A",
      "configurationADescription": "The baseline configuration for this run.",
      "configurationB": "Configuration B",
      "configurationBDescription":
        "Compare a different model or settings against A.",
      "configurationsMustDiffer": "Configurations A and B must differ.",
      "configurationsSectionDescription":
        "Run one configuration or add a second for a side-by-side comparison.",
      "configurationsSectionTitle": "Configure the run",
      "createSample": "Create a sample",
      "description":
        "Choose source audio, configure a model, and optionally compare a second configuration.",
      "includeThoughts": "Capture thinking",
      "includeThoughtsHint":
        "Returns the model's internal reasoning steps (Gemini only). Useful for investigating thinking-budget blowouts.",
      "model": "Model",
      "modelPlaceholder": "Select a model",
      "noSamples": "No samples yet.",
      "noSamplesHint":
        "Create a ground-truth sample to measure word error rate.",
      "prompt": "Prompt override",
      "promptPlaceholder":
        "Optional: override the default transcription prompt",
      "readySummary_one": "Ready to run {{count}} configuration.",
      "readySummary_other": "Ready to run {{count}} configurations.",
      "recordingDuration": "{{minutes}} min",
      "recordingFilter": {
        "all": "All recordings (longest first)",
        "failedAttempts": "With failed transcription attempts",
      },
      "recordingFilterLabel": "Filter",
      "recordingNoDuration": "—",
      "recordingNoFailedRecordings": "No recordings have failed attempts.",
      "recordingNoRecordings": "No matching recordings.",
      "recordingNoRecordingsHint":
        "Change the filter or return after eligible audio has been uploaded.",
      "recordingNoTranscription": "No transcription",
      "recordingPlaceholder": "Select a recording (longest first)",
      "recordingSource": "Recording from DB",
      "recordingStatus": "Last transcription: {{status}}",
      "recordingStatusCompleted": "Completed",
      "recordingStatusFailed": "Failed",
      "recordingStatusPending": "Pending",
      "removeComparison": "Remove",
      "run": "Run benchmark",
      "runComparison": "Run comparison",
      "runSummary_one": "{{count}} configuration",
      "runSummary_other": "{{count}} configurations",
      "sample": "Ground-truth sample",
      "sampleHint":
        "Each sample bundles audio with its reference transcript. Every provider is scored (WER) against it.",
      "samplePlaceholder": "Select a sample",
      "sampling": {
        "frequency_penalty": "Frequency penalty",
        "frequency_penaltyInfo":
          "Discourages tokens more as they repeat. Higher values reduce repetition.",
        "hint":
          "Blank = model default. Applied to this configuration's Gemini call only.",
        "presence_penalty": "Presence penalty",
        "presence_penaltyInfo":
          "Discourages tokens already used, regardless of frequency. Higher values encourage new wording.",
        "temperature": "Temperature",
        "temperatureInfo":
          "Controls randomness. Higher values produce more varied output.",
        "title": "Sampling overrides",
        "top_p": "Top-P",
        "top_pInfo":
          "Limits choices to the most likely tokens. Lower values produce more focused output.",
      },
      "selectBaselineToContinue": "Select a model for configuration A.",
      "selectComparisonToContinue":
        "Select a model for configuration B or remove the comparison.",
      "selectRecordingToContinue": "Select a recording to continue.",
      "selectSampleToContinue": "Select a sample to continue.",
      "showAllRecordings": "Show all recordings",
      "sourceHint":
        "Sample = audio with a reference transcript for WER scoring. Recording = any recording from the DB, no reference.",
      "sourceModeRecording": "Recording from DB",
      "sourceModeSample": "Sample with reference",
      "sourceSectionTitle": "Choose source audio",
      "thinking": {
        "HIGH": "High",
        "LOW": "Low",
        "MEDIUM": "Medium",
        "MINIMAL": "Minimal",
        "off": "Off",
      },
      "thinkingLevel": "Thinking level",
      "title": "New transcription benchmark",
    },
    "show": {
      "configuration": "Configuration",
      "diffToggle": "Diff vs reference",
      "enabled": "Enabled",
      "hidePrompt": "Hide prompt",
      "reference": "Reference",
      "rerun": "Rerun",
      "responseDialog": {
        "cachedTokens": "Cached tokens",
        "completionTokens": "Completion tokens",
        "iteration": "Iteration",
        "noTokenUsage": "No token usage recorded for this run.",
        "noTrace": "No request trace recorded for this run.",
        "promptTokens": "Prompt tokens",
        "rawResponse": "Raw response",
        "textContent": "Text content",
        "thoughtsTokens": "Thoughts tokens",
        "tokenUsage": "Token usage",
        "totalTokens": "Total tokens",
        "trace": "Trace",
        "triggerLabel": "Response",
        "words": "Words",
      },
      "showPrompt": "Show prompt",
    },
    "status": {
      "completed": "Completed",
      "failed": "Failed",
      "pending": "Pending",
      "running": "Running",
    },
  },
  "transcriptionBenchmarkSamples": {
    "edit": {
      "replaceAudio": "Replace audio (optional)",
      "replaceAudioHint":
        "Leave empty to keep the current audio. Choosing a file replaces it.",
      "save": "Save changes",
      "title": "Edit ground-truth sample",
    },
    "index": {
      "confirmDelete": "Delete this sample?",
      "createdAt": "Created",
      "duration": "Duration",
      "empty": "No samples yet. Create one to benchmark providers against it.",
      "label": "Label",
      "newSample": "New sample",
      "title": "Ground-truth samples",
    },
    "new": {
      "audioFile": "Audio file",
      "label": "Label",
      "labelPlaceholder": "e.g. Bad — noisy handpiece",
      "redactionHint":
        "Paste the corrected reference transcript exactly as the audio should be transcribed. It is stored verbatim and used as the WER ground truth.",
      "reference": "Reference transcript (optional)",
      "referenceOptionalHint":
        "Optional: leave blank for audio-only uploads with no WER scoring (e.g. when investigating MAX_TOKENS blowouts).",
      "referencePlaceholder": "Paste the corrected ground-truth transcript",
      "save": "Save sample",
      "title": "New ground-truth sample",
    },
  },
  "unsavedChanges": {
    "confirmLeave":
      "You have unsaved changes. Do you really want to leave this page?",
  },
  "uploads": {
    "backToRecordings": "Back to recordings",
    "cta": {
      "start": "Start upload",
      "uploading": "Uploading...",
    },
    "description":
      "Practice: {{practiceName}}. Drag an audio file here or choose a file.",
    "dropzone": {
      "subtitle": "or click to select an audio file",
      "title": "Drop file here",
    },
    "emptyAudioFile":
      "The audio file is empty. Please select or create a new recording.",
    "failed": "Upload failed",
    "invalidAudioFile": "Please select a valid audio file.",
    "item": {
      "error": "Failed",
      "pending": "Ready",
      "success": "Done",
      "uploading": "Uploading...",
    },
    "recordingCreateFailed": "Could not create recording.",
    "removeSelectedFile": "Remove selected file",
    "signedOut":
      "You were signed out. The recording is saved. Sign in again in a new tab.",
    "submitFailed": "The file could not be uploaded successfully.",
    "success": "File uploaded successfully.",
    "title": "Upload audio files",
  },
  "versions": {
    "events": {
      "create": "Created",
      "destroy": "Deleted",
      "update": "Updated",
    },
    "fields": {
      "additional_address": "Additional address",
      "address": "Address",
      "admin": "Admin",
      "amount_cents": "Amount",
      "analog_description": "Analog description",
      "annotated_private": "Annotated (private)",
      "annotated_public": "Annotated (public)",
      "apply_complexes": "Apply complexes",
      "audio_quality_level": "Audio quality",
      "audio_upload_enabled": "Audio upload enabled",
      "billing_exempt": "Billing exempt",
      "can_review_billing_suggestions": "Can review billing",
      "city": "City",
      "class_name": "Class name",
      "classification": "Classification",
      "confidence": "Confidence",
      "content": "Content",
      "deactivated_at": "Deactivated at",
      "description": "Description",
      "email": "Email",
      "factor": "Factor",
      "first_name": "First name",
      "improved": "Structured documentation",
      "improved_original": "Improved version",
      "included_for_sync": "Included for sync",
      "insurance": "Insurance",
      "integration_ivoris_entry_id": "Ivoris entry ID",
      "integration_ivoris_patient_id": "Ivoris patient ID",
      "integration_ivoris_synchronised_at": "Ivoris synced at",
      "integration_teemer_chunking": "Teemer chunking",
      "integration_teemer_domain": "Teemer domain",
      "integration_teemer_seal_short_name": "Teemer seal",
      "integration_teemer_session_id": "Teemer session ID",
      "integration_teemer_synchronised_at": "Teemer synced at",
      "key": "Key",
      "kzv": "KZV",
      "laboratory_tasks": "Laboratory tasks",
      "last_name": "Last name",
      "name": "Name",
      "notes": "Notes",
      "original": "Original transcript",
      "patient_id": "Patient ID",
      "patient_id_numeric": "Patient number",
      "position_code": "Position code",
      "pvs_system": "PVS system",
      "reason": "Reason",
      "recorded_at": "Recorded at",
      "reference_factor": "Reference factor",
      "report_message": "Report message",
      "retention_period_audio": "Audio retention period",
      "role": "Role",
      "short_summary": "Short summary",
      "source": "Source",
      "status": "Status",
      "summary": "Summary",
      "synchronization_status": "Sync status",
      "template": "Template",
      "title": "Title",
      "transcript": "Transcript",
      "transcription_provider": "Transcription provider",
      "treated_at": "Treated at",
      "trigger_words": "Trigger words",
      "value": "Value",
      "zip": "ZIP",
    },
    "filters": {
      "allTypes": "All types",
      "clearRecording": "Remove recording {{id}} filter",
      "date": "Date",
      "prefixIdPlaceholder": "Record ID",
      "recording": "Recording {{id}}",
      "recordTypePlaceholder": "Record type",
      "userPlaceholder": "Search by user",
    },
    "index": {
      "deletedUser": "Deleted user",
      "empty": "No audit trail entries found.",
      "table": {
        "changes": "Changes",
        "date": "Date",
        "event": "Event",
        "model": "Record",
        "user": "User",
      },
      "title": "Audit trail",
      "viaCustomizationChat": "via setup assistant",
    },
    "itemTypes": {
      "AnalogPosition": "Analog position",
      "Assistant": "Assistant",
      "BillingPosition": "Billing position",
      "Combination": "Combination",
      "CustomPrompt": "Custom prompt",
      "DentalChart": "Dental chart",
      "Exclusion": "Exclusion",
      "Justification": "Justification",
      "KzvbRule": "KZVB rule",
      "LaboratoryPrice": "Lab price",
      "LaboratoryService": "Lab service",
      "OnboardingPackage": "Onboarding package",
      "Practice": "Practice",
      "PracticeAnalogPosition": "Practice analog position",
      "PracticeComplex": "Complex",
      "PracticeDefaultFactor": "Default factor",
      "PracticeMembership": "Practice membership",
      "Recording": "Recording",
      "RegulationPosition": "Regulation position",
      "Setting": "Setting",
      "Treatment": "Treatment",
      "User": "User",
    },
    "sheet": {
      "noChanges": "No field changes recorded.",
      "redacted": "Changed (content not stored)",
      "title": "Change details",
    },
  },
} as const;
